import { useAuth } from '@/auth/useAuth';
import { MatrizInline } from '@/components/MatrizInline';
import { TernaRevisionStepper, isStepPendiente } from '@/components/revision/TernaRevisionStepper';
import { RevisionFileModal } from '@/components/revision/RevisionFileModal';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { CorreccionesSplitPanel } from '@/components/asesorias/CorreccionesSplitPanel';
import { DropdownMenu, DropdownMenuItem, Input, Spinner } from '@/components/ui';
import { AsesoriaHistorialTable } from '@/components/ui/AsesoriaHistorialTable';
import { Modal } from '@/components/ui/Modal';
import { OficialPrefix } from '@/components/ui/OficialHint';
import { Tooltip } from '@/components/ui/Tooltip';
import {
  useAprobarAsesoria,
  useDesmarcarAutoAprobar,
  useEvaluacionAdmin,
  useLatestHistorialMap,
  useMarcarAutoAprobar,
  usePendientesVerificacion,
  useRechazarAsesoria,
  useSimularAprobarAsesoria,
  useSimularEvaluarAsesoria,
} from '@/hooks';
import { useAdminSecretPanel } from '@/hooks/useAdminSecretPanel';
import { buildHistorialFieldMap, type HistorialMap } from '@/hooks/useLatestHistorialMap';
import { ApiError } from '@/services/api';
import { dashboardService, type ElementoBloqueante, type SimularAprobarResponse } from '@/services/dashboard.service';
import type { CambioFieldLog, DiffActivaTernaCambios } from '@/types/evaluacionAdmin';
import type { AsesoriaPendiente } from '@/types/types';
import { buildTernaVeredictoPayload } from '@/utils/revisionHelpers';
import { comentariosRestantesRonda, estadoConversacion, historialKey, type RevisionEntry } from '@/utils/revisionHistorial';
import { agruparCadenaArchivos } from '@/utils/fileChains';
import { formatDuracionCorta } from '@/utils/formatDuration';
import { CorreccionesChat } from '@/components/asesorias/CorreccionesChat';
import { ShiftActionOverlay } from '@/components/revision/ShiftActionOverlay';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Check,
  CheckCircle,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Download,
  ExternalLink,
  Headphones,
  Info,
  LayoutGrid,
  List,
  Maximize2,
  Minimize2,
  Paperclip,
  Trash2,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type RevTab = 'evaluacion' | 'evidencias' | 'audios' | 'trazabilidad' | 'historial';

// ─── Helpers ────────────────────────────────────────

function formatHours(h: number): string {
  const t = Math.round(h * 3600);
  const hrs = Math.floor(t / 3600);
  const min = Math.floor((t % 3600) / 60);
  if (hrs === 0) return `${min}m`;
  return `${hrs}h ${min}m`;
}

function formatDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function parseRecomendaciones(text?: string | null) {
  if (!text) return { original: null, rechazos: [] };
  const lines = text.split('\n');
  const rechazos: { fecha: string; motivo: string }[] = [];
  const originalLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const match = line.match(/^\[(.*?)\] RECHAZO ADMIN:(?:\s*(.*))?/);
    if (match) {
      let motivo = match[2] || '';
      if (!motivo && i + 1 < lines.length && !lines[i + 1].match(/^\[(.*?)\] RECHAZO ADMIN:/)) {
        motivo = lines[i + 1].trim();
        i++; // skip next line
      }
      rechazos.push({ fecha: match[1], motivo });
    } else {
      originalLines.push(line);
    }
  }

  const original = originalLines.join('\n').trim();
  return { original: original || null, rechazos };
}

function formatDateTime(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Veredicto de las evidencias/audios VIGENTES (la hoja de cada cadena de reemplazo). Las versiones
 * intermedias de una cadena quedan en la base con su estado viejo (PENDIENTE/RECHAZADO) aunque ya
 * fueron superadas; considerarlas rompía los guards de los botones (p.ej. "Devolver" quedaba
 * deshabilitado con todo revisado). Espejo del criterio de conteo de las tabs.
 */
export function veredictoItemsVigentes(asesoria: Pick<AsesoriaPendiente, 'evidenciasGenerales' | 'audios'>): {
  hayPendientes: boolean;
  hayRechazados: boolean;
  evidenciasPendientes: AsesoriaPendiente['evidenciasGenerales'];
  audiosPendientes: AsesoriaPendiente['audios'];
  evidenciasRechazadas: AsesoriaPendiente['evidenciasGenerales'];
  audiosRechazados: AsesoriaPendiente['audios'];
} {
  const evidencias = agruparCadenaArchivos(
    (asesoria.evidenciasGenerales ?? []).filter((e) => e.estado !== 'ELIMINADO'),
    (e) => e.evidenciaId,
    (e) => e.reemplazaEvidenciaId ?? null
  ).map((c) => c.actual);
  const audios = agruparCadenaArchivos(
    asesoria.audios ?? [],
    (a) => a.audioId,
    (a) => a.reemplazaAudioId ?? null
  ).map((c) => c.actual);
  const esPendiente = (estado?: string | null) => estado === 'PENDIENTE';

  const evidenciasPendientes = evidencias.filter((e) => esPendiente(e.estado));
  const audiosPendientes = audios.filter((a) => esPendiente(a.estado));
  const evidenciasRechazadas = evidencias.filter((e) => e.estado === 'RECHAZADO');
  const audiosRechazados = audios.filter((a) => a.estado === 'RECHAZADO');

  return {
    hayPendientes: evidenciasPendientes.length > 0 || audiosPendientes.length > 0,
    hayRechazados: evidenciasRechazadas.length > 0 || audiosRechazados.length > 0,
    evidenciasPendientes,
    audiosPendientes,
    evidenciasRechazadas,
    audiosRechazados,
  };
}

export function getDetallesBloqueo(asesoria?: AsesoriaPendiente | null, evalData?: any, latestHistorialMap?: HistorialMap) {
  if (!asesoria) {
    return {
      evidenciasPendientes: [],
      audiosPendientesCount: 0,
      requisitosPendientesNumerals: [],
      requisitosPendientesDescriptions: [] as string[],
      evidenciasRechazadas: [],
      audiosRechazadosCount: 0,
      requisitosRechazadosNumerals: [],
      requisitosRechazadosDescriptions: [] as string[],
      totalPendientesCount: 0,
      totalRechazadosCount: 0,
    };
  }

  const veredicto = veredictoItemsVigentes(asesoria);

  const requisitosPendientesMap = new Map<string, string>();
  const requisitosRechazadosMap = new Map<string, string>();

  if (evalData?.evaluacionesTernas) {
    for (const et of evalData.evaluacionesTernas) {
      for (const reqCell of et.requisitos ?? []) {
        if (!reqCell || !reqCell.numeral) continue;
        if (!reqCell.evaluacionRequisito) continue;

        const evalReqId = reqCell.evaluacionRequisito.id;
        const historialEntry = latestHistorialMap?.get(`${evalReqId}:REQUISITO`);

        const califAsesoria = reqCell.evaluacionRequisito.calificacion;
        const califBaseline = reqCell.calificacionBaseline ?? reqCell.calificacion;
        const estadoRev = historialEntry?.estado ?? reqCell.evaluacionRequisito.estadoRevision;

        if (estadoRev !== 'RECHAZADO' && califAsesoria && califBaseline && califAsesoria === califBaseline) {
          continue;
        }

        const label = reqCell.capitulo ? `${reqCell.numeral}. ${reqCell.capitulo}` : reqCell.numeral;
        if (!estadoRev || estadoRev === 'PENDIENTE') {
          requisitosPendientesMap.set(reqCell.numeral, label);
        } else if (estadoRev === 'RECHAZADO') {
          requisitosRechazadosMap.set(reqCell.numeral, label);
        }
      }

      if (et.id && latestHistorialMap) {
        const obsEntry = latestHistorialMap.get(`${et.id}:OBSERVACIONES`);
        if (obsEntry?.estado === 'RECHAZADO') {
          requisitosRechazadosMap.set(`obs-${et.id}`, 'Observaciones');
        } else if (obsEntry?.estado === 'PENDIENTE' || obsEntry?.estado === 'RESPUESTA_ASESOR' || obsEntry?.estado === 'COMENTADO') {
          requisitosPendientesMap.set(`obs-${et.id}`, 'Observaciones');
        }

        const planEntry = latestHistorialMap.get(`${et.id}:PLANMEJORA`);
        if (planEntry?.estado === 'RECHAZADO') {
          requisitosRechazadosMap.set(`plan-${et.id}`, 'Plan de Mejora');
        } else if (planEntry?.estado === 'PENDIENTE' || planEntry?.estado === 'RESPUESTA_ASESOR' || planEntry?.estado === 'COMENTADO') {
          requisitosPendientesMap.set(`plan-${et.id}`, 'Plan de Mejora');
        }

        const fechaEntry = latestHistorialMap.get(`${et.id}:FECHAIMPLEMENTACION`);
        if (fechaEntry?.estado === 'RECHAZADO') {
          requisitosRechazadosMap.set(`fecha-${et.id}`, 'Fecha de Implementación');
        } else if (fechaEntry?.estado === 'PENDIENTE' || fechaEntry?.estado === 'RESPUESTA_ASESOR' || fechaEntry?.estado === 'COMENTADO') {
          requisitosPendientesMap.set(`fecha-${et.id}`, 'Fecha de Implementación');
        }
      }
    }
  }

  const evidenciasPendientes = veredicto.evidenciasPendientes.map((e) => e.nombre);
  const evidenciasRechazadas = veredicto.evidenciasRechazadas.map((e) => e.nombre);
  const requisitosPendientesNumerals = Array.from(requisitosPendientesMap.keys());
  const requisitosPendientesDescriptions = Array.from(requisitosPendientesMap.values());
  const requisitosRechazadosNumerals = Array.from(requisitosRechazadosMap.keys());
  const requisitosRechazadosDescriptions = Array.from(requisitosRechazadosMap.values());

  return {
    evidenciasPendientes,
    audiosPendientesCount: veredicto.audiosPendientes.length,
    requisitosPendientesNumerals,
    requisitosPendientesDescriptions,
    evidenciasRechazadas,
    audiosRechazadosCount: veredicto.audiosRechazados.length,
    requisitosRechazadosNumerals,
    requisitosRechazadosDescriptions,
    totalPendientesCount: evidenciasPendientes.length + veredicto.audiosPendientes.length + requisitosPendientesNumerals.length,
    totalRechazadosCount: evidenciasRechazadas.length + veredicto.audiosRechazados.length + requisitosRechazadosNumerals.length,
  };
}

/**
 * `requiereResubir` de la última versión REVISADA de la cadena (la más reciente con valor no nulo).
 * Al re-revisar una evidencia/audio, el checkbox "requiere volver a subir" se pre-marca con lo que
 * se decidió la última vez — no con `true` fijo ni con la hoja re-subida (que suele venir en null).
 */
export function ultimoRequiereResubir(versiones: { requiereResubir?: boolean | null }[]): boolean {
  for (let i = versiones.length - 1; i >= 0; i--) {
    const r = versiones[i]?.requiereResubir;
    if (r != null) return r;
  }
  return true;
}

// ─── Modals ─────────────────────────────────────────

export function AprobarModal({
  asesoria,
  onClose,
  onConfirm,
  isLoading,
  error,
  showSecretPanel,
  onSimular,
  simulando,
  simulacionResultado,
}: {
  asesoria: AsesoriaPendiente;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  error: string | null;
  /** Modo debug (ctrl+.): agrega un botón para validar la aprobación sin persistir nada. */
  showSecretPanel?: boolean;
  onSimular?: () => void;
  simulando?: boolean;
  simulacionResultado?: SimularAprobarResponse | null;
}) {
  const { data: evalDataModal } = useEvaluacionAdmin(asesoria.asesoriaId);
  const detallesBloqueo = useMemo(() => getDetallesBloqueo(asesoria, evalDataModal), [asesoria, evalDataModal]);

  const [verTodo, setVerTodo] = useState(false);

  // Auto-simulate on mount to get the authoritative list of blocking elements from the backend
  const { simular } = useSimularAprobarAsesoria();
  const [autoSimResult, setAutoSimResult] = useState<SimularAprobarResponse | null>(null);
  const [autoSimLoading, setAutoSimLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAutoSimLoading(true);
      const result = await simular(asesoria.asesoriaId);
      if (!cancelled) {
        setAutoSimResult(result);
        setAutoSimLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [asesoria.asesoriaId, simular]);

  // Use simulation elementos as the source of truth; fall back to frontend detallesBloqueo
  const simElementos = autoSimResult && !autoSimResult.ok ? (autoSimResult.elementos ?? []) : [];
  const simMensaje = autoSimResult && !autoSimResult.ok ? (autoSimResult.mensaje ?? autoSimResult.message ?? null) : null;

  const hayBloqueo = simElementos.length > 0 || detallesBloqueo.totalPendientesCount > 0 || !!error;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-xs" onClick={onClose}>
      <div
        className="bg-(--admin-surface) border border-(--admin-border) rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 font-['DM_Sans'] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-(--admin-green-bg) rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-(--admin-green)" />
          </div>
          <h3 className="text-[16px] font-semibold text-(--admin-text)">Aprobar Asesoría</h3>
        </div>
        <p className="text-(--admin-text2) text-[13px] mb-1">
          ¿Confirmas la aprobación de la asesoría de <strong>{asesoria.empresa.nombre}</strong>?
        </p>
        <p className="text-[11px] text-(--admin-text3) mb-4">
          Asesor: {asesoria.asesor.nombre} · {formatHours(asesoria.horas)}
        </p>
        {autoSimLoading && (
          <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200 text-[11px] text-gray-500 flex items-center gap-2">
            <Spinner className="w-3.5 h-3.5" />
            <span>Verificando elegibilidad...</span>
          </div>
        )}
        {!autoSimLoading && hayBloqueo && (
          <div className="mb-4 p-3 rounded-lg bg-[#fdf0ee] border border-[#f5c6c0] text-[11px] text-[#c0392b]">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4.5 h-4.5 shrink-0 mt-0.5" />
              <span className="font-semibold">
                {error ||
                  simMensaje ||
                  `No se puede aprobar: hay ${simElementos.length || detallesBloqueo.totalPendientesCount} elemento(s) sin revisar`}
              </span>
            </div>
            {simElementos.length > 0 && (
              <>
                {!verTodo ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[10px] text-[#a93226]">{simElementos.length} elemento(s) pendiente(s)</span>
                    <button
                      type="button"
                      onClick={() => setVerTodo(true)}
                      className="text-[10px] font-bold underline text-[#c0392b] hover:text-[#922b21] transition-colors"
                    >
                      Ver todo ▾
                    </button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-bold text-[#a93226] uppercase tracking-wide">Detalle de pendientes</span>
                      <button
                        type="button"
                        onClick={() => setVerTodo(false)}
                        className="text-[10px] font-bold underline text-[#c0392b] hover:text-[#922b21] transition-colors"
                      >
                        Ocultar ▴
                      </button>
                    </div>
                    <ul className="space-y-1 max-h-48 overflow-y-auto">
                      {simElementos.map((el, i) => (
                        <li key={`${el.tipo}-${el.id}-${i}`} className="bg-white/60 rounded px-2 py-1">
                          <span className="font-bold uppercase tracking-wide text-[9px] mr-1.5 whitespace-pre-line">{el.tipo}</span>
                          {el.nombre}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {showSecretPanel && (
          <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Modo debug</span>
              <button
                type="button"
                onClick={onSimular}
                disabled={simulando}
                className="admin-btn text-[11px] py-1 px-2.5 border-amber-300 bg-white text-amber-700 hover:bg-amber-100 disabled:opacity-50"
              >
                {simulando ? 'Simulando...' : 'Simular aprobación'}
              </button>
            </div>
            <p className="text-[10px] text-amber-700/80">Valida si la aprobación real pasaría, sin persistir ningún cambio.</p>
            {simulacionResultado && (
              <div
                className={`mt-2 p-2.5 rounded-md text-[11px] ${
                  simulacionResultado.ok ? 'bg-(--admin-green-bg) text-(--admin-green)' : 'bg-(--admin-red-bg) text-(--admin-red)'
                }`}
              >
                <p className="font-semibold">{simulacionResultado.mensaje ?? simulacionResultado.message}</p>
                {simulacionResultado.elementos && simulacionResultado.elementos.length > 0 && (
                  <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                    {simulacionResultado.elementos.map((el, i) => (
                      <li key={`${el.tipo}-${el.id}-${i}`} className="bg-white/60 rounded px-2 py-1">
                        <span className="font-bold uppercase tracking-wide text-[9px] mr-1.5">{el.tipo}</span>
                        {el.nombre}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
        <div className="flex gap-3">
          <button type="button" className="admin-btn flex-1 justify-center" onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>
          <button
            type="button"
            className="admin-btn bg-(--admin-green-bg) text-(--admin-green) border-transparent flex-1 justify-center font-bold"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Aprobando...' : 'Aprobar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DenegarModal({
  asesoria,
  onClose,
  onConfirm,
  isLoading,
}: {
  asesoria: AsesoriaPendiente;
  onClose: () => void;
  onConfirm: (motivo?: string, permiteSubirEvidenciasNuevas?: boolean) => void;
  isLoading: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  // Por defecto el asesor puede subir evidencias nuevas (comportamiento previo). El admin
  // desmarca para restringir a solo reemplazar las rechazadas.
  const [permiteSubirEvidenciasNuevas, setPermiteSubirEvidenciasNuevas] = useState(true);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 backdrop-blur-xs" onClick={onClose}>
      <div
        className="bg-(--admin-surface) border border-(--admin-border) rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 font-['DM_Sans'] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-(--admin-red-bg) rounded-full flex items-center justify-center">
            <XCircle className="w-5 h-5 text-(--admin-red)" />
          </div>
          <h3 className="text-[16px] font-semibold text-(--admin-text)">Denegar Asesoría</h3>
        </div>
        <p className="text-(--admin-text2) text-[13px] mb-4">
          La asesoría de <strong>{asesoria.empresa.nombre}</strong> será devuelta al asesor para correcciones.
        </p>
        <div className="mb-6">
          <label htmlFor="motivo" className="block text-[12px] font-medium text-(--admin-text2) mb-1">
            Motivo (opcional)
          </label>
          <textarea
            id="motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Observación de devolución..."
            className="w-full px-3 py-2 border border-(--admin-border2) rounded-lg text-[13px] resize-none focus:outline-2 focus:outline-[#1a1a1a] focus:outline-offset-1 font-['DM_Sans'] bg-white text-gray-800"
            rows={3}
          />
        </div>
        <label className="flex items-center gap-2 text-[12px] text-(--admin-text2) mb-6 cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="denegar-permite-subir-evidencias"
            checked={permiteSubirEvidenciasNuevas}
            onChange={(e) => setPermiteSubirEvidenciasNuevas(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span>
            Permitir subir evidencias nuevas{' '}
            <span className="text-(--admin-text3)">(si lo desmarcas, el asesor solo podrá reemplazar las rechazadas)</span>
          </span>
        </label>
        <div className="flex gap-3">
          <button type="button" className="admin-btn flex-1 justify-center" onClick={onClose} disabled={isLoading}>
            Cancelar
          </button>
          <button
            type="button"
            className="admin-btn bg-(--admin-red-bg) text-(--admin-red) border-transparent flex-1 justify-center font-bold"
            onClick={() => onConfirm(motivo || undefined, permiteSubirEvidenciasNuevas)}
            disabled={isLoading}
          >
            {isLoading ? 'Denegando...' : 'Denegar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Tab Content: Evaluación NTC ───────────────────

function EstadoRevision({ guardando, ultimoGuardado }: { guardando: boolean; ultimoGuardado: Date | null }) {
  if (guardando) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--admin-blue)' }}>
        <div
          className="w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: 'var(--admin-blue)', borderTopColor: 'transparent' }}
        />
        <span className="text-[12px] font-medium">Guardando...</span>
      </div>
    );
  }
  if (ultimoGuardado) {
    return (
      <div className="flex items-center gap-2" style={{ color: 'var(--admin-green)' }}>
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-[12px] font-medium">Revisión guardada</span>
      </div>
    );
  }
  return null;
}

// ─── Tab Content: Evaluacion ─────────────────────

export function EvalTab({
  asesoriaId,
  nit,
  refetch: refetchParent,
  isFullscreen: _isFullscreen,
  onHasRejections,
}: {
  asesoriaId: number;
  nit: string;
  refetch: () => void;
  isFullscreen?: boolean;
  onHasRejections?: (v: boolean) => void;
}) {
  const { data, isLoading, error, refetch } = useEvaluacionAdmin(asesoriaId);
  const [historialRevision, setHistorialRevision] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [ultimoGuardado, setUltimoGuardado] = useState<Date | null>(null);
  const LS_VIEW_KEY = 'admin:revisionView';
  const [revisionView, setRevisionView] = useState<'matrix' | 'stepper'>(() =>
    localStorage.getItem(LS_VIEW_KEY) === 'stepper' ? 'stepper' : 'matrix'
  );
  const latestHistorialMap = useLatestHistorialMap(historialRevision as any);

  useEffect(() => {
    const hasAny = Array.from(latestHistorialMap.values()).some((v) => v.estado === 'RECHAZADO');
    onHasRejections?.(hasAny);
  }, [latestHistorialMap, onHasRejections]);

  const diffTernaMap = useMemo(() => {
    const m = new Map<string, DiffActivaTernaCambios>();
    const diffTernas = data?.diffActiva?.ternas;
    if (!diffTernas) return m;
    if (Array.isArray(diffTernas)) {
      diffTernas.forEach((d: any) => {
        const cambios = d.cambios || d;
        if (d.ternaId) m.set(`terna:${d.ternaId}`, cambios);
        if (d.grupo) m.set(`grupo:${d.grupo}`, cambios);
      });
    } else {
      Object.entries(diffTernas).forEach(([id, val]: [string, any]) => {
        const key = !isNaN(Number(id)) ? `terna:${id}` : `grupo:${id}`;
        const cambios: DiffActivaTernaCambios = {};
        if (typeof val.observaciones === 'string') {
          cambios.observaciones = { activa: val.observaciones, anterior: null };
        } else if (val.observaciones) {
          cambios.observaciones = val.observaciones;
        }
        if (typeof val.planMejora === 'string') {
          cambios.planMejora = { activa: val.planMejora, anterior: null };
        } else if (val.planMejora) {
          cambios.planMejora = val.planMejora;
        }
        if (val.fechaImplementacion) cambios.fechaImplementacion = val.fechaImplementacion;
        if (val.evidenciaIds) cambios.evidenciaIds = val.evidenciaIds;
        m.set(key, cambios);
      });
    }
    return m;
  }, [data?.diffActiva]);

  const firstPendingStepIndex = useMemo(() => {
    if (!data?.evaluacionesTernas) return 0;
    const steps = data.evaluacionesTernas
      .map((et) => {
        const byId = et.ternaId ? `terna:${et.ternaId}` : null;
        const byGrupo = et.grupo ? `grupo:${et.grupo}` : null;
        const key = byId && diffTernaMap.has(byId) ? byId : byGrupo && diffTernaMap.has(byGrupo) ? byGrupo : null;
        if (!key) return null;
        return { evalTerna: et, diffTerna: diffTernaMap.get(key)! };
      })
      .filter(Boolean) as { evalTerna: (typeof data.evaluacionesTernas)[0]; diffTerna: DiffActivaTernaCambios }[];
    const idx = steps.findIndex((s) => isStepPendiente(s.diffTerna, s.evalTerna.id, latestHistorialMap));
    return idx >= 0 ? idx : 0;
  }, [data?.evaluacionesTernas, diffTernaMap, latestHistorialMap]);

  const refetchHistorial = useCallback(() => {
    if (nit && asesoriaId) {
      dashboardService.getRevisionHistorial(nit, asesoriaId).then((res) => {
        if (res.success) setHistorialRevision(res.historial);
      });
    }
  }, [nit, asesoriaId]);

  const onStepSaved = useCallback(
    (_savedIds: number[]) => {
      refetch();
      refetchHistorial();
    },
    [refetch, refetchHistorial]
  );

  useEffect(() => {
    refetchHistorial();
  }, [refetchHistorial]);

  // Sin batch: cada Aprobar/Rechazar se guarda de inmediato (una llamada por elemento).
  // Se puede seguir editando después — el backend sobrescribe el mismo turno abierto.
  const guardarInmediato = useCallback(
    async (fn: () => Promise<unknown>) => {
      setGuardando(true);
      try {
        await fn();
        refetch();
        refetchParent();
        refetchHistorial();
        setUltimoGuardado(new Date());
      } finally {
        setGuardando(false);
      }
    },
    [refetch, refetchParent, refetchHistorial]
  );

  const onTernaFieldVeredicto = useCallback(
    async (
      evalTernaId: number,
      campo: 'observaciones' | 'planMejora' | 'fechaImplementacion',
      estado: 'APROBADO' | 'RECHAZADO' | 'COMENTADO',
      observacion?: string
    ) => {
      await guardarInmediato(() =>
        dashboardService.revisarTernasCamposAsesoria(asesoriaId, [{ evaluacionTernaId: evalTernaId, campo, estado, observacion }])
      );
    },
    [asesoriaId, guardarInmediato]
  );

  const onRequisitoVeredicto = useCallback(
    async (evaluacionRequisitoId: number, estado: 'APROBADO' | 'RECHAZADO', observacion?: string) => {
      await guardarInmediato(() =>
        dashboardService.revisarRequisitosAsesoria(asesoriaId, [{ evaluacionRequisitoId, estado, observacion }])
      );
    },
    [asesoriaId, guardarInmediato]
  );

  if (isLoading)
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="md" />
        <span className="ml-3 text-[13px] text-(--admin-text3)">Cargando evaluación...</span>
      </div>
    );
  if (error)
    return (
      <div className="p-6 text-center">
        <XCircle className="w-6 h-6 text-(--admin-red) mx-auto mb-2" />
        <p className="text-[13px] text-(--admin-red)">{error}</p>
        <button type="button" className="admin-btn mt-3 text-[11px]" onClick={refetch}>
          Reintentar
        </button>
      </div>
    );
  if (!data) return null;

  return (
    <div className="space-y-6 animate-in fade-in-50 duration-200">
      {/* Unified Matrix / Stepper */}
      <div className="bg-white border border-(--admin-border) rounded-xl shadow-xs overflow-x-auto">
        {/* Barra superior: estado (izquierda) y toggle de vista (derecha) */}
        <div className="sticky top-0 z-20 flex items-center justify-between gap-3 px-4 py-2.5 border-b border-gray-100 bg-white/95 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <EstadoRevision guardando={guardando} ultimoGuardado={ultimoGuardado} />
          </div>
          <div role="group" aria-label="Vista de revisión" className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
            <button
              type="button"
              aria-pressed={revisionView === 'matrix'}
              onClick={() => {
                setRevisionView('matrix');
                localStorage.setItem(LS_VIEW_KEY, 'matrix');
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                revisionView === 'matrix' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              Matriz
            </button>
            <button
              type="button"
              aria-pressed={revisionView === 'stepper'}
              onClick={() => {
                setRevisionView('stepper');
                localStorage.setItem(LS_VIEW_KEY, 'stepper');
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                revisionView === 'stepper' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <ChevronRight className="w-3.5 h-3.5" />
              Paso a paso
            </button>
          </div>
        </div>

        {revisionView === 'matrix' ? (
          <div className="p-4">
            <MatrizInline
              data={data}
              isRevisionMode={true}
              onTernaFieldVeredicto={onTernaFieldVeredicto}
              onRequisitoVeredicto={onRequisitoVeredicto}
              historialRevision={historialRevision}
            />
          </div>
        ) : (
          <TernaRevisionStepper
            asesoriaId={asesoriaId}
            data={data}
            diffTernaMap={diffTernaMap}
            latestHistorialMap={latestHistorialMap}
            onStepSaved={onStepSaved}
            initialIndex={firstPendingStepIndex}
            historialRevision={historialRevision}
            onTernaFieldVeredicto={onTernaFieldVeredicto}
            onRequisitoVeredicto={onRequisitoVeredicto}
          />
        )}
      </div>
    </div>
  );
}

// ─── Tab Content: Evidencias ──────────────────────

export function EvidenciasTab({
  asesoria,
  refetch,
  canReview = true,
}: {
  asesoria: AsesoriaPendiente;
  refetch: () => void;
  canReview?: boolean;
}) {
  const { user } = useAuth();
  const [historialRevision, setHistorialRevision] = useState<any[]>([]);
  const [accesoModal, setAccesoModal] = useState<{ tipo: 'evidencia' | 'audio'; id: number } | null>(null);
  const [reviewModalEv, setReviewModalEv] = useState<any | null>(null);
  const [accesoEmail, setAccesoEmail] = useState('');
  const [solicitandoAcceso, setSolicitandoAcceso] = useState(false);
  const [accesoError, setAccesoError] = useState<string | null>(null);
  const [accesoExito, setAccesoExito] = useState<string | null>(null);
  const [guardandoTodas, setGuardandoTodas] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [viewMode, setViewMode] = useState<'split' | 'grid'>('split');

  const cerrarAccesoModal = () => {
    setAccesoModal(null);
    setAccesoEmail('');
    setAccesoError(null);
    setAccesoExito(null);
  };

  const refetchHistorial = useCallback(() => {
    dashboardService.getRevisionHistorial(asesoria.empresa.nitId, asesoria.asesoriaId).then((res) => {
      if (res.success) setHistorialRevision(res.historial as any[]);
    });
  }, [asesoria.empresa.nitId, asesoria.asesoriaId]);

  useEffect(() => {
    refetchHistorial();
  }, [refetchHistorial]);

  const historialFieldMap = useMemo(() => buildHistorialFieldMap(historialRevision as any), [historialRevision]);

  // Cada reemplazo de evidencia es un borrado lógico: la fila vieja queda en la base
  // (RECHAZADO) y la nueva apunta a ella vía reemplazaEvidenciaId. Sin agrupar por cadena,
  // cada versión aparecería como una tarjeta separada aunque sea "la misma" evidencia.
  const cadenas = useMemo(
    () =>
      agruparCadenaArchivos(
        asesoria.evidenciasGenerales.filter((e) => e.estado !== 'ELIMINADO'),
        (e) => e.evidenciaId,
        (e) => e.reemplazaEvidenciaId ?? null
      ),
    [asesoria.evidenciasGenerales]
  );

  // Evidencias que el asesor eliminó — el backend ya no las excluye del payload, así que se
  // muestran aparte (informativo para el admin) pero nunca entran en cadenas/conteos.
  const eliminadas = useMemo(() => asesoria.evidenciasGenerales.filter((e) => e.estado === 'ELIMINADO'), [asesoria.evidenciasGenerales]);

  // True si la cadena tuvo algún RECHAZADO. Combinado con "vigente PENDIENTE" distingue una
  // corrección re-enviada (esperando re-revisión) de una evidencia nueva nunca revisada.
  const cadenaTuvoRechazo = useCallback(
    (version: { evidenciaId: number }[]) =>
      version.some((v) =>
        ((historialFieldMap.get(historialKey('EVIDENCIA', v.evidenciaId)) ?? []) as any[]).some((e) => e.estado === 'RECHAZADO')
      ),
    [historialFieldMap]
  );

  const stats = useMemo(() => {
    let aprobadas = 0;
    let rechazadas = 0;
    let corregidas = 0;
    let nuevas = 0;
    for (const { actual, version } of cadenas) {
      if (actual.estado === 'APROBADO') aprobadas++;
      else if (actual.estado === 'RECHAZADO') rechazadas++;
      else if (cadenaTuvoRechazo(version)) corregidas++;
      else nuevas++;
    }
    return { total: cadenas.length, aprobadas, rechazadas, corregidas, nuevas };
  }, [cadenas, cadenaTuvoRechazo]);

  const eliminadasSection = eliminadas.length > 0 && (
    <div className="px-4 py-3 border-t border-(--admin-border)">
      <h4 className="text-[11px] font-semibold text-(--admin-text3) uppercase tracking-wide mb-2">Eliminadas</h4>
      <ul className="space-y-1.5">
        {eliminadas.map((ev) => {
          const historial = historialFieldMap.get(historialKey('EVIDENCIA', ev.evidenciaId)) ?? [];
          const delEntry = historial.find((h) => h.estado === 'SISTEMA_EVIDENCIA_ELIMINADA');
          // Un RECHAZADO en el historial de ESTA evidencia, ya ELIMINADO, solo puede venir del
          // camino nuevo: el admin la rechazó sin un asesor al que devolverle la corrección
          // (asesor reasignado) — ver revisarEvidencias en el backend, que la elimina directo en
          // vez de dejarla en RECHAZADO. Por default se asume el caso de siempre (el asesor la
          // borró él mismo), consistente con el comportamiento previo a este cambio.
          const rechazoAdminEntry = historial.find((h) => h.estado === 'RECHAZADO');
          const fechaBorrado = delEntry?.fecha ?? rechazoAdminEntry?.fecha ?? ev.fechaAdicion;
          const etiqueta = rechazoAdminEntry ? 'Eliminada (asesor reasignado)' : 'Eliminada por el asesor';
          return (
            <li key={ev.evidenciaId} className="flex items-center gap-2 flex-wrap">
              <span className="text-[12px] font-medium text-gray-500 truncate">{ev.nombre}</span>
              <span className="admin-chip text-[10px] bg-gray-100 text-gray-500 border-transparent px-1.5 py-0.5 rounded-full font-semibold">
                {etiqueta}
              </span>
              <span className="text-[11px] text-(--admin-text3)">{formatDate(fechaBorrado)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  if (cadenas.length === 0) {
    return (
      <div className="animate-in fade-in-50 duration-200">
        <div className="py-8 text-center text-(--admin-text3) text-[13px]">
          <Paperclip className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Sin evidencias cargadas
        </div>
        {eliminadasSection}
      </div>
    );
  }

  const pendientesEvids = cadenas.map((c) => c.actual).filter((ev) => !ev.estado || ev.estado === 'PENDIENTE' || ev.estado === 'ACTIVO');
  const hasPendientes = pendientesEvids.length > 0;

  return (
    <div className="animate-in fade-in-50 duration-200">
      {canReview && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-b border-(--admin-border) bg-gray-50/50">
          <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className="font-semibold text-(--admin-text)">
              {stats.total} evidencia{stats.total !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#b7dfc7] bg-[#eaf5ee] px-2 py-0.5 font-semibold text-[#2d7a4f]">
              <CheckCircle className="w-3 h-3" /> {stats.aprobadas} aprobada{stats.aprobadas !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#f5c6c0] bg-[#fdf0ee] px-2 py-0.5 font-semibold text-[#c0392b]">
              <XCircle className="w-3 h-3" /> {stats.rechazadas} rechazada{stats.rechazadas !== 1 ? 's' : ''}
            </span>
            {stats.corregidas > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-2 py-0.5 font-semibold text-[#1d4ed8]">
                <AlertCircle className="w-3 h-3" /> {stats.corregidas} corregida{stats.corregidas !== 1 ? 's' : ''}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-[#fef08a] bg-[#fef9c3] px-2 py-0.5 font-semibold text-[#854d0e]">
              <AlertCircle className="w-3 h-3" /> {stats.nuevas} pendiente{stats.nuevas !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-200/80 p-0.5 rounded-lg text-gray-600">
              <button
                type="button"
                onClick={() => setViewMode('split')}
                className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                  viewMode === 'split' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                }`}
                title="Vista detalle en panel"
              >
                <List className="w-3.5 h-3.5" />
                Detalle
              </button>
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-md transition-all cursor-pointer ${
                  viewMode === 'grid' ? 'bg-white text-gray-900 shadow-xs' : 'text-gray-500 hover:text-gray-900'
                }`}
                title="Parrilla rápida de aprobación (Soporta Shift+Hover)"
              >
                <LayoutGrid className="w-3.5 h-3.5" />
                Parrilla Rápida
              </button>
            </div>
            {hasPendientes && (
              <button
                type="button"
                disabled={guardandoTodas}
                onClick={async () => {
                  setGuardandoTodas(true);
                  try {
                    const payload = pendientesEvids.map((e) => ({ evidenciaId: e.evidenciaId, estado: 'APROBADO' as const }));
                    await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, payload);
                    refetch();
                    refetchHistorial();
                  } finally {
                    setGuardandoTodas(false);
                  }
                }}
                className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-green-200 bg-white text-green-700 hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50 cursor-pointer"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                {guardandoTodas ? 'Guardando...' : 'Aprobar todas'}
              </button>
            )}
          </div>
        </div>
      )}

      {cadenas.length > 0 && viewMode === 'grid' && (
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 animate-in fade-in-50 duration-200">
          {cadenas.map(({ actual: ev, version }) => {
            const isPendiente = !ev.estado || ev.estado === 'PENDIENTE' || ev.estado === 'ACTIVO';
            const isAprobada = ev.estado === 'APROBADO';
            const isRechazada = ev.estado === 'RECHAZADO';
            const fueCorregida = isPendiente && cadenaTuvoRechazo(version);

            const handleAprobarEv = async () => {
              await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [{ evidenciaId: ev.evidenciaId, estado: 'APROBADO' }]);
              refetch();
              refetchHistorial();
            };

            const handleQuickRechazarEv = async () => {
              await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [{ evidenciaId: ev.evidenciaId, estado: 'RECHAZADO' }]);
              refetch();
              refetchHistorial();
            };

            const handleRechazarEvModal = () => {
              setReviewModalEv(ev);
            };

            return (
              <ShiftActionOverlay
                key={ev.evidenciaId}
                onAprobar={canReview ? handleAprobarEv : undefined}
                onRechazar={canReview ? handleQuickRechazarEv : undefined}
              >
                <div className="p-3.5 rounded-xl border border-gray-200 bg-white shadow-xs hover:border-gray-300 hover:shadow-md transition-all flex flex-col justify-between gap-3 h-full">
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className="text-[13px] font-bold text-gray-900 wrap-break-word flex-1">{ev.nombre}</span>
                      {isAprobada && (
                        <span className="shrink-0 text-[10px] bg-[#eaf5ee] text-[#2d7a4f] border border-[#b7dfc7] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> Aprobada
                        </span>
                      )}
                      {isRechazada && (
                        <span className="shrink-0 text-[10px] bg-[#fdf0ee] text-[#c0392b] border border-[#f5c6c0] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <XCircle className="w-3 h-3" /> Rechazada
                        </span>
                      )}
                      {fueCorregida && (
                        <span className="shrink-0 text-[10px] bg-[#eff6ff] text-[#1d4ed8] border border-[#bfdbfe] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Corregida
                        </span>
                      )}
                      {isPendiente && !fueCorregida && (
                        <span className="shrink-0 text-[10px] bg-[#fef9c3] text-[#854d0e] border border-[#fef08a] px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Pendiente
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">Cargada el {formatDate(ev.fechaAdicion)}</p>
                  </div>

                  {canReview && (
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={handleRechazarEvModal}
                        className="flex-1 py-1 px-2.5 rounded-lg border border-red-200 bg-red-50/50 text-red-700 hover:bg-red-100 text-[11px] font-bold transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Rechazar
                      </button>
                      <button
                        type="button"
                        onClick={handleAprobarEv}
                        className="flex-1 py-1 px-2.5 rounded-lg border border-green-200 bg-green-50/50 text-green-700 hover:bg-green-100 text-[11px] font-bold transition-colors flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <CheckCircle className="w-3.5 h-3.5" />
                        Aprobar
                      </button>
                    </div>
                  )}
                </div>
              </ShiftActionOverlay>
            );
          })}
        </div>
      )}

      {cadenas.length > 0 && viewMode === 'split' && (
        <CorreccionesSplitPanel
          items={cadenas}
          selectedIndex={Math.min(activeIdx, cadenas.length - 1)}
          onSelect={setActiveIdx}
          getKey={(c) => c.actual.evidenciaId}
          getItemOverlayProps={(c) => ({
            onAprobar: canReview
              ? async () => {
                  await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [
                    { evidenciaId: c.actual.evidenciaId, estado: 'APROBADO' },
                  ]);
                  refetch();
                  refetchHistorial();
                }
              : undefined,
            onRechazar: canReview
              ? async () => {
                  await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [
                    { evidenciaId: c.actual.evidenciaId, estado: 'RECHAZADO' },
                  ]);
                  refetch();
                  refetchHistorial();
                }
              : undefined,
          })}
          renderItem={(c) => {
            const est = c.actual.estado;
            const pend = !est || est === 'PENDIENTE' || est === 'ACTIVO';
            const corregida = pend && cadenaTuvoRechazo(c.version);
            const dot =
              est === 'APROBADO' ? 'bg-[#2d7a4f]' : est === 'RECHAZADO' ? 'bg-[#c0392b]' : corregida ? 'bg-[#1d4ed8]' : 'bg-[#854d0e]';
            return (
              <>
                <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                <span className="flex-1 min-w-0 truncate text-[12px] font-medium text-gray-800">{c.actual.nombre}</span>
              </>
            );
          }}
          renderPanel={({ actual: ev, inicial, version, siguientePorId }) => {
            const isPendiente = !ev.estado || ev.estado === 'PENDIENTE' || ev.estado === 'ACTIVO';
            const isAprobada = ev.estado === 'APROBADO';
            const isRechazada = ev.estado === 'RECHAZADO';
            // Pendiente + hubo rechazo previo = el asesor ya corrigió y espera re-revisión.
            const fueCorregida = isPendiente && cadenaTuvoRechazo(version);
            const tieneReemplazos = version.length > 1;
            // Apelación pendiente: el asesor respondió y el turno quedó del lado del admin. En ese
            // caso el botón invita a "Revisar" (hay algo nuevo), no a "Modificar revisión".
            const entriesCadena = version.flatMap(
              (v) => (historialFieldMap.get(historialKey('EVIDENCIA', v.evidenciaId)) ?? []) as unknown as RevisionEntry[]
            );
            const hayApelacionPendiente = estadoConversacion(entriesCadena) === 'ESPERANDO_ADMIN';

            return (
              <div key={ev.evidenciaId} className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  {canReview && isAprobada && <CheckCircle className="w-4 h-4 text-(--admin-green) shrink-0" />}
                  {canReview && isRechazada && <XCircle className="w-4 h-4 text-(--admin-red) shrink-0" />}
                  {canReview && isPendiente && <AlertCircle className="w-4 h-4 text-[#854d0e] shrink-0" />}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13px] font-medium text-(--admin-text) truncate">{ev.nombre}</span>
                      {canReview && isAprobada && (
                        <span className="admin-chip text-[10px] bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7] px-1.5 py-0.5 rounded-full font-semibold">
                          Aprobada
                        </span>
                      )}
                      {canReview && isRechazada && (
                        <span className="admin-chip text-[10px] bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0] px-1.5 py-0.5 rounded-full font-semibold">
                          Rechazada
                        </span>
                      )}
                      {canReview && isPendiente && fueCorregida && (
                        <span className="admin-chip text-[10px] bg-[#eff6ff] text-[#1d4ed8] border-[#bfdbfe] px-1.5 py-0.5 rounded-full font-semibold">
                          Corregida · esperando revisión
                        </span>
                      )}
                      {canReview && isPendiente && !fueCorregida && (
                        <span className="admin-chip text-[10px] bg-[#fef9c3] text-[#854d0e] border-[#fef08a] px-1.5 py-0.5 rounded-full font-semibold">
                          Pendiente
                        </span>
                      )}
                    </div>
                    {ev.descripcion && (
                      <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-2" title={ev.descripcion}>
                        {ev.descripcion}
                      </div>
                    )}
                    <div className="text-[11px] text-(--admin-text3) mt-0.5">
                      {formatDate(ev.fechaAdicion)}
                      {canReview && isRechazada && ev.observacion && (
                        <span className="block mt-1 italic text-(--admin-red) whitespace-pre-wrap wrap-break-word">"{ev.observacion}"</span>
                      )}
                    </div>
                    {tieneReemplazos && (
                      <a
                        href={inicial.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 hover:bg-gray-100 transition-colors w-fit max-w-full"
                      >
                        <Paperclip className="w-3 h-3 shrink-0" />
                        <span className="font-semibold shrink-0">Archivo inicial:</span>
                        <span className="truncate">{inicial.nombre}</span>
                      </a>
                    )}
                    {(() => {
                      const entries = version.flatMap(
                        (v) => (historialFieldMap.get(historialKey('EVIDENCIA', v.evidenciaId)) ?? []) as unknown as RevisionEntry[]
                      );
                      if (entries.length === 0) return null;
                      // El admin conserva el link a TODAS las versiones (no se "olvidan" como al asesor).
                      const conArchivo = entries.map((e) => {
                        if (e.estado !== 'SISTEMA_EVIDENCIA_SUBIDA' || e.elementoId == null) return e;
                        const siguiente = siguientePorId.get(Number(e.elementoId));
                        if (!siguiente) return e;
                        return {
                          ...e,
                          archivoAdjunto: { nombre: siguiente.nombre, url: siguiente.url, descripcion: siguiente.descripcion ?? null },
                        };
                      });
                      return (
                        <CorreccionesChat
                          entries={conArchivo}
                          actorActual="admin"
                          canReply={canReview}
                          comentariosRestantes={comentariosRestantesRonda(conArchivo)}
                          className="mt-1.5 w-full"
                          onReply={async (texto) => {
                            await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [
                              { evidenciaId: ev.evidenciaId, estado: 'COMENTADO', observacion: texto },
                            ]);
                            refetch();
                            refetchHistorial();
                          }}
                        />
                      );
                    })()}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0 self-end">
                  <a href={ev.url} target="_blank" rel="noopener noreferrer" className="admin-btn text-[11px] py-1 px-2.5">
                    <ExternalLink className="w-3 h-3" /> Ver
                  </a>
                  {canReview && (
                    <button
                      type="button"
                      onClick={() => setReviewModalEv(ev)}
                      className="admin-btn text-[11px] py-1 px-2.5 flex items-center gap-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-semibold"
                    >
                      <CheckSquare className="w-3.5 h-3.5" />
                      {(isAprobada || isRechazada) && !hayApelacionPendiente ? 'Modificar revisión' : 'Revisar'}
                    </button>
                  )}
                </div>
              </div>
            );
          }}
        />
      )}

      {eliminadasSection}

      <Modal isOpen={accesoModal !== null} onClose={() => !solicitandoAcceso && cerrarAccesoModal()} title="Obtener acceso al archivo">
        <div className="space-y-4">
          {accesoExito ? (
            <>
              <p className="text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{accesoExito}</p>
              <div className="flex justify-end mt-4">
                <button type="button" onClick={cerrarAccesoModal} className="admin-btn primary">
                  Cerrar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
                Ingresa el correo al que quieres otorgar acceso de lectura en Drive. Si lo dejas vacío, se usará el correo de tu cuenta.
              </p>
              <Input
                label="Correo electrónico (opcional)"
                type="email"
                placeholder={user?.email ?? 'tu correo'}
                value={accesoEmail}
                onChange={(e) => {
                  setAccesoEmail(e.target.value);
                  setAccesoError(null);
                }}
                disabled={solicitandoAcceso}
              />
              {accesoError && (
                <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{accesoError}</p>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={cerrarAccesoModal} disabled={solicitandoAcceso} className="admin-btn disabled:opacity-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={solicitandoAcceso}
                  className="admin-btn primary disabled:opacity-50"
                  onClick={async () => {
                    if (!accesoModal) return;
                    setSolicitandoAcceso(true);
                    setAccesoError(null);
                    try {
                      const res = await dashboardService.solicitarAccesoArchivo(
                        accesoModal.tipo,
                        accesoModal.id,
                        accesoEmail.trim() || (user?.email ?? '')
                      );
                      setAccesoExito(res.message);
                    } catch (e) {
                      setAccesoError(e instanceof Error ? e.message : 'Error al solicitar acceso');
                    } finally {
                      setSolicitandoAcceso(false);
                    }
                  }}
                >
                  {solicitandoAcceso ? 'Solicitando...' : 'Solicitar acceso'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {reviewModalEv && (
        <RevisionFileModal
          open={reviewModalEv !== null}
          onClose={() => setReviewModalEv(null)}
          titulo={reviewModalEv.nombre}
          kind="evidencia"
          descripcion={reviewModalEv.descripcion}
          fileUrl={reviewModalEv.url}
          historialList={(() => {
            const cadena = cadenas.find((c) => c.actual.evidenciaId === reviewModalEv.evidenciaId);
            const ids = cadena ? cadena.version.map((v) => v.evidenciaId) : [reviewModalEv.evidenciaId];
            return ids.flatMap((id) => historialFieldMap.get(historialKey('EVIDENCIA', id)) ?? []);
          })()}
          estadoActual={
            reviewModalEv.estado === 'APROBADO' || reviewModalEv.estado === 'RECHAZADO'
              ? (reviewModalEv.estado as 'APROBADO' | 'RECHAZADO')
              : undefined
          }
          observacionActual={reviewModalEv.observacion ?? ''}
          requiereResubirActual={ultimoRequiereResubir(
            cadenas.find((c) => c.actual.evidenciaId === reviewModalEv.evidenciaId)?.version ?? [reviewModalEv]
          )}
          onAprobar={async (observacion) => {
            await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [
              { evidenciaId: reviewModalEv.evidenciaId, estado: 'APROBADO', observacion, requiereResubir: false },
            ]);
            refetch();
            refetchHistorial();
          }}
          onRechazar={async (observacion, requiereResubir) => {
            await dashboardService.revisarEvidenciasAsesoria(asesoria.asesoriaId, [
              { evidenciaId: reviewModalEv.evidenciaId, estado: 'RECHAZADO', observacion, requiereResubir },
            ]);
            refetch();
            refetchHistorial();
          }}
          onAcceso={() => {
            setAccesoEmail('');
            setAccesoModal({ tipo: 'evidencia', id: reviewModalEv.evidenciaId });
          }}
        />
      )}
    </div>
  );
}

// ─── Tab Content: Audios ──────────────────────────

export function AudiosTab({
  asesoria,
  refetch,
  canReview = true,
}: {
  asesoria: AsesoriaPendiente;
  refetch: () => void;
  canReview?: boolean;
}) {
  const { user } = useAuth();
  const [historialRevision, setHistorialRevision] = useState<any[]>([]);
  const [accesoModal, setAccesoModal] = useState<{ tipo: 'evidencia' | 'audio'; id: number } | null>(null);
  const [reviewModalAu, setReviewModalAu] = useState<any | null>(null);
  const [accesoEmail, setAccesoEmail] = useState('');
  const [solicitandoAcceso, setSolicitandoAcceso] = useState(false);
  const [accesoError, setAccesoError] = useState<string | null>(null);
  const [accesoExito, setAccesoExito] = useState<string | null>(null);
  const [guardandoTodos, setGuardandoTodos] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  // El audio original (reemplazado) no es lo que se revisa — mantenerlo colapsado por defecto
  // evita que compita visualmente con el audio actual, que es el único que importa para aprobar
  // o rechazar. Se guarda por audioId de la cadena para no perder el estado al cambiar de panel.
  const [originalesExpandidos, setOriginalesExpandidos] = useState<Set<number>>(new Set());

  const cerrarAccesoModal = () => {
    setAccesoModal(null);
    setAccesoEmail('');
    setAccesoError(null);
    setAccesoExito(null);
  };

  const refetchHistorial = useCallback(() => {
    dashboardService.getRevisionHistorial(asesoria.empresa.nitId, asesoria.asesoriaId).then((res) => {
      if (res.success) setHistorialRevision(res.historial as any[]);
    });
  }, [asesoria.empresa.nitId, asesoria.asesoriaId]);

  useEffect(() => {
    refetchHistorial();
  }, [refetchHistorial]);

  const historialFieldMap = useMemo(() => buildHistorialFieldMap(historialRevision as any), [historialRevision]);

  // Mismo criterio que EvidenciasTab: cada reemplazo de audio es un borrado lógico
  // (fila vieja intacta + reemplazaAudioId), así que agrupamos por cadena.
  const cadenas = useMemo(
    () =>
      agruparCadenaArchivos(
        asesoria.audios,
        (a) => a.audioId,
        (a) => a.reemplazaAudioId ?? null
      ),
    [asesoria.audios]
  );

  // True si la cadena de audio tuvo algún RECHAZADO (para distinguir "corregido, esperando
  // revisión" de un audio nuevo sin revisar cuando la versión vigente está PENDIENTE).
  const cadenaTuvoRechazo = useCallback(
    (version: { audioId: number }[]) =>
      version.some((v) => ((historialFieldMap.get(historialKey('AUDIO', v.audioId)) ?? []) as any[]).some((e) => e.estado === 'RECHAZADO')),
    [historialFieldMap]
  );

  const stats = useMemo(() => {
    let aprobados = 0;
    let rechazados = 0;
    let corregidos = 0;
    let nuevos = 0;
    for (const { actual, version } of cadenas) {
      if (actual.estado === 'APROBADO') aprobados++;
      else if (actual.estado === 'RECHAZADO') rechazados++;
      else if (cadenaTuvoRechazo(version)) corregidos++;
      else nuevos++;
    }
    return { total: cadenas.length, aprobados, rechazados, corregidos, nuevos };
  }, [cadenas, cadenaTuvoRechazo]);

  if (cadenas.length === 0) {
    return (
      <div className="py-8 text-center text-(--admin-text3) text-[13px] animate-in fade-in-50 duration-200">
        <Headphones className="w-8 h-8 mx-auto mb-2 opacity-30" />
        Sin audios registrados
      </div>
    );
  }

  const pendientesAudios = cadenas
    .map((c) => c.actual)
    .filter((audio) => !audio.estado || audio.estado === 'PENDIENTE' || audio.estado === 'ACTIVO');
  const hasPendientes = pendientesAudios.length > 0;

  return (
    <div className="animate-in fade-in-50 duration-200">
      {canReview && (
        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-b border-(--admin-border) bg-gray-50/50">
          <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
            <span className="font-semibold text-(--admin-text)">
              {stats.total} audio{stats.total !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#b7dfc7] bg-[#eaf5ee] px-2 py-0.5 font-semibold text-[#2d7a4f]">
              <CheckCircle className="w-3 h-3" /> {stats.aprobados} aprobado{stats.aprobados !== 1 ? 's' : ''}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-[#f5c6c0] bg-[#fdf0ee] px-2 py-0.5 font-semibold text-[#c0392b]">
              <XCircle className="w-3 h-3" /> {stats.rechazados} rechazado{stats.rechazados !== 1 ? 's' : ''}
            </span>
            {stats.corregidos > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#bfdbfe] bg-[#eff6ff] px-2 py-0.5 font-semibold text-[#1d4ed8]">
                <AlertCircle className="w-3 h-3" /> {stats.corregidos} corregido{stats.corregidos !== 1 ? 's' : ''}
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-full border border-[#fef08a] bg-[#fef9c3] px-2 py-0.5 font-semibold text-[#854d0e]">
              <AlertCircle className="w-3 h-3" /> {stats.nuevos} pendiente{stats.nuevos !== 1 ? 's' : ''}
            </span>
          </div>
          {hasPendientes && (
            <button
              type="button"
              disabled={guardandoTodos}
              onClick={async () => {
                setGuardandoTodos(true);
                try {
                  const payload = pendientesAudios.map((a) => ({ audioId: a.audioId, estado: 'APROBADO' as const }));
                  await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, payload);
                  refetch();
                  refetchHistorial();
                } finally {
                  setGuardandoTodos(false);
                }
              }}
              className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-green-200 bg-white text-green-700 hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50"
            >
              <CheckCircle className="w-3.5 h-3.5" />
              {guardandoTodos ? 'Guardando...' : 'Aprobar todos'}
            </button>
          )}
        </div>
      )}
      {/* Mismo layout split que evidencias/asesor. El label del item es la fecha y hora de subida
          + la duración (no el id del audio). El admin conserva link a Drive de todas las versiones. */}
      <CorreccionesSplitPanel
        items={cadenas}
        selectedIndex={Math.min(activeIdx, cadenas.length - 1)}
        onSelect={setActiveIdx}
        getKey={(c) => c.actual.audioId}
        getItemOverlayProps={(c) => ({
          onAprobar: canReview
            ? async () => {
                await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, [{ audioId: c.actual.audioId, estado: 'APROBADO' }]);
                refetch();
                refetchHistorial();
              }
            : undefined,
          onRechazar: canReview
            ? async () => {
                await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, [{ audioId: c.actual.audioId, estado: 'RECHAZADO' }]);
                refetch();
                refetchHistorial();
              }
            : undefined,
        })}
        renderItem={(c) => {
          const est = c.actual.estado;
          const pend = !est || est === 'PENDIENTE' || est === 'ACTIVO';
          const corregido = pend && cadenaTuvoRechazo(c.version);
          const dot =
            est === 'APROBADO' ? 'bg-[#2d7a4f]' : est === 'RECHAZADO' ? 'bg-[#c0392b]' : corregido ? 'bg-[#1d4ed8]' : 'bg-[#854d0e]';
          return (
            <>
              <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
              <span className="flex-1 min-w-0 truncate text-[12px] font-medium text-gray-800">
                {formatDate(c.actual.fechaGrabacion)} · {formatDuracionCorta(c.actual.duracionSeg)}
              </span>
            </>
          );
        }}
        renderPanel={({ actual: audio, inicial, version, siguientePorId }) => {
          const isAprobado = audio.estado === 'APROBADO';
          const isRechazado = audio.estado === 'RECHAZADO';
          const isPendiente = !audio.estado || audio.estado === 'PENDIENTE' || audio.estado === 'ACTIVO';
          const fueCorregido = isPendiente && cadenaTuvoRechazo(version);
          const tieneReemplazos = version.length > 1;
          // Apelación pendiente: el asesor respondió y el turno quedó del lado del admin → "Revisar".
          const entriesCadena = version.flatMap(
            (v) => (historialFieldMap.get(historialKey('AUDIO', v.audioId)) ?? []) as unknown as RevisionEntry[]
          );
          const hayApelacionPendiente = estadoConversacion(entriesCadena) === 'ESPERANDO_ADMIN';

          return (
            <div key={audio.audioId} className="flex flex-col gap-2">
              <div className="flex items-center gap-3">
                {canReview && isAprobado && <CheckCircle className="w-4 h-4 text-(--admin-green) shrink-0" />}
                {canReview && isRechazado && <XCircle className="w-4 h-4 text-(--admin-red) shrink-0" />}
                {canReview && isPendiente && <AlertCircle className="w-4 h-4 text-[#854d0e] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-(--admin-text)">
                      {formatDateTime(audio.fechaGrabacion)} · {formatDuracionCorta(audio.duracionSeg)}
                    </span>
                    {canReview && isAprobado && (
                      <span className="admin-chip text-[10px] bg-[#eaf5ee] text-[#2d7a4f] border-[#b7dfc7] px-1.5 py-0.5 rounded-full font-semibold">
                        Aprobado
                      </span>
                    )}
                    {canReview && isRechazado && (
                      <span className="admin-chip text-[10px] bg-[#fdf0ee] text-[#c0392b] border-[#f5c6c0] px-1.5 py-0.5 rounded-full font-semibold">
                        Rechazado
                      </span>
                    )}
                    {canReview && isPendiente && fueCorregido && (
                      <span className="admin-chip text-[10px] bg-[#eff6ff] text-[#1d4ed8] border-[#bfdbfe] px-1.5 py-0.5 rounded-full font-semibold">
                        Corregido · esperando revisión
                      </span>
                    )}
                    {canReview && isPendiente && !fueCorregido && (
                      <span className="admin-chip text-[10px] bg-[#fef9c3] text-[#854d0e] border-[#fef08a] px-1.5 py-0.5 rounded-full font-semibold">
                        Pendiente
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-(--admin-text3) mt-0.5">
                    Asesor: {audio.asesorNombre}
                    {canReview && isRechazado && audio.observacion && (
                      <span className="block mt-1 italic text-(--admin-red) whitespace-pre-wrap wrap-break-word">
                        "{audio.observacion}"
                      </span>
                    )}
                  </div>

                  <a
                    href={`https://drive.google.com/file/d/${audio.adjunteDelAudio}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir audio en Google Drive"
                    className="admin-btn text-[11px] py-1 px-2.5 flex items-center gap-1 w-fit mt-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> Abrir audio
                  </a>

                  <div className="mt-2 w-full max-w-2xl">
                    <AudioPlayer
                      fileIdOrUrl={audio.adjunteDelAudio}
                      initialDuration={audio.duracionSeg}
                      // El link a Drive ya va arriba, junto a la fecha/duración — no duplicarlo aquí.
                      showDriveLink={false}
                      compact={false}
                      className="bg-white border-gray-200"
                    />
                  </div>

                  {tieneReemplazos &&
                    (() => {
                      const expandido = originalesExpandidos.has(audio.audioId);
                      // TODA la cadena reemplazada, no solo `inicial` (el primero): con 2+
                      // reemplazos (3+ audios) había versiones intermedias que no aparecían en
                      // ningún lado. `version` viene en orden cronológico y ya incluye el actual
                      // al final — se excluye con slice(0, -1).
                      const anteriores = version.slice(0, -1);
                      return (
                        <div className="mt-1.5 w-fit max-w-full">
                          <button
                            type="button"
                            onClick={() =>
                              setOriginalesExpandidos((prev) => {
                                const next = new Set(prev);
                                if (next.has(audio.audioId)) next.delete(audio.audioId);
                                else next.add(audio.audioId);
                                return next;
                              })
                            }
                            className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600 transition-colors px-1 py-0.5"
                          >
                            {expandido ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                            {anteriores.length === 1
                              ? 'Audio original (reemplazado)'
                              : `${anteriores.length} audios anteriores (reemplazados)`}
                          </button>
                          {expandido && (
                            <div className="mt-1 flex flex-col gap-1">
                              {anteriores.map((v, i) => (
                                <a
                                  key={v.audioId}
                                  href={`https://drive.google.com/file/d/${v.adjunteDelAudio}/view`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 hover:bg-gray-100 transition-colors w-fit max-w-full"
                                >
                                  <Headphones className="w-3 h-3 shrink-0" />
                                  <span className="font-semibold shrink-0">{i === 0 ? 'Audio inicial' : `Versión ${i + 1}`}</span>
                                  <span className="shrink-0">· {formatDuracionCorta(v.duracionSeg)}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  {(() => {
                    const entries = version.flatMap(
                      (v) => (historialFieldMap.get(historialKey('AUDIO', v.audioId)) ?? []) as unknown as RevisionEntry[]
                    );
                    if (entries.length === 0) return null;
                    const conArchivo = entries.map((e) => {
                      if (e.estado !== 'SISTEMA_AUDIO_VINCULADO' || e.elementoId == null) return e;
                      const siguiente = siguientePorId.get(Number(e.elementoId));
                      if (!siguiente) return e;
                      return {
                        ...e,
                        archivoAdjunto: {
                          nombre: `Audio · ${siguiente.asesorNombre}`,
                          url: `https://drive.google.com/file/d/${siguiente.adjunteDelAudio}/view`,
                          duracionSeg: siguiente.duracionSeg,
                        },
                      };
                    });
                    return (
                      <CorreccionesChat
                        entries={conArchivo}
                        actorActual="admin"
                        canReply={canReview}
                        comentariosRestantes={comentariosRestantesRonda(conArchivo)}
                        className="mt-1.5 w-full"
                        onReply={async (texto) => {
                          await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, [
                            { audioId: audio.audioId, estado: 'COMENTADO', observacion: texto },
                          ]);
                          refetch();
                          refetchHistorial();
                        }}
                      />
                    );
                  })()}
                </div>
              </div>

              <div className="flex items-center gap-1.5 shrink-0 self-end">
                {canReview && (
                  <button
                    type="button"
                    onClick={() => setReviewModalAu(audio)}
                    className="admin-btn text-[11px] py-1 px-2.5 flex items-center gap-1 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 font-semibold"
                  >
                    <CheckSquare className="w-3.5 h-3.5" />
                    {(isAprobado || isRechazado) && !hayApelacionPendiente ? 'Modificar revisión' : 'Revisar'}
                  </button>
                )}
              </div>
            </div>
          );
        }}
      />

      <Modal isOpen={accesoModal !== null} onClose={() => !solicitandoAcceso && cerrarAccesoModal()} title="Obtener acceso al archivo">
        <div className="space-y-4">
          {accesoExito ? (
            <>
              <p className="text-[13px] text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{accesoExito}</p>
              <div className="flex justify-end mt-4">
                <button type="button" onClick={cerrarAccesoModal} className="admin-btn primary">
                  Cerrar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[13px]" style={{ color: 'var(--admin-text2)' }}>
                Ingresa el correo al que quieres otorgar acceso de lectura en Drive. Si lo dejas vacío, se usará el correo de tu cuenta.
              </p>
              <Input
                label="Correo electrónico (opcional)"
                type="email"
                placeholder={user?.email ?? 'tu correo'}
                value={accesoEmail}
                onChange={(e) => {
                  setAccesoEmail(e.target.value);
                  setAccesoError(null);
                }}
                disabled={solicitandoAcceso}
              />
              {accesoError && (
                <p className="text-[12px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{accesoError}</p>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={cerrarAccesoModal} disabled={solicitandoAcceso} className="admin-btn disabled:opacity-50">
                  Cancelar
                </button>
                <button
                  type="button"
                  disabled={solicitandoAcceso}
                  className="admin-btn primary disabled:opacity-50"
                  onClick={async () => {
                    if (!accesoModal) return;
                    setSolicitandoAcceso(true);
                    setAccesoError(null);
                    try {
                      const res = await dashboardService.solicitarAccesoArchivo(
                        accesoModal.tipo,
                        accesoModal.id,
                        accesoEmail.trim() || (user?.email ?? '')
                      );
                      setAccesoExito(res.message);
                    } catch (e) {
                      setAccesoError(e instanceof Error ? e.message : 'Error al solicitar acceso');
                    } finally {
                      setSolicitandoAcceso(false);
                    }
                  }}
                >
                  {solicitandoAcceso ? 'Solicitando...' : 'Solicitar acceso'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {reviewModalAu && (
        <RevisionFileModal
          open={reviewModalAu !== null}
          onClose={() => setReviewModalAu(null)}
          titulo={`Audio de ${reviewModalAu.asesorNombre}`}
          kind="audio"
          descripcion={`Grabado el ${new Date(reviewModalAu.fechaGrabacion).toLocaleDateString()}`}
          fileUrl={`https://drive.google.com/file/d/${reviewModalAu.adjunteDelAudio}/view`}
          audioDuration={reviewModalAu.duracionSeg}
          historialList={(() => {
            const cadena = cadenas.find((c) => c.actual.audioId === reviewModalAu.audioId);
            const ids = cadena ? cadena.version.map((v) => v.audioId) : [reviewModalAu.audioId];
            return ids.flatMap((id) => historialFieldMap.get(historialKey('AUDIO', id)) ?? []);
          })()}
          estadoActual={
            reviewModalAu.estado === 'APROBADO' || reviewModalAu.estado === 'RECHAZADO'
              ? (reviewModalAu.estado as 'APROBADO' | 'RECHAZADO')
              : undefined
          }
          observacionActual={reviewModalAu.observacion ?? ''}
          requiereResubirActual={ultimoRequiereResubir(
            cadenas.find((c) => c.actual.audioId === reviewModalAu.audioId)?.version ?? [reviewModalAu]
          )}
          onAprobar={async (observacion) => {
            await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, [
              { audioId: reviewModalAu.audioId, estado: 'APROBADO', observacion, requiereResubir: false },
            ]);
            refetch();
            refetchHistorial();
          }}
          onRechazar={async (observacion, requiereResubir) => {
            await dashboardService.revisarAudiosAsesoria(asesoria.asesoriaId, [
              { audioId: reviewModalAu.audioId, estado: 'RECHAZADO', observacion, requiereResubir },
            ]);
            refetch();
            refetchHistorial();
          }}
          onAcceso={() => {
            setAccesoEmail('');
            setAccesoModal({ tipo: 'audio', id: reviewModalAu.audioId });
          }}
        />
      )}
    </div>
  );
}

export function TrazabilidadTab({ asesoriaId }: { asesoriaId: number }) {
  const [cambios, setCambios] = useState<CambioFieldLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [campoFilter, setCampoFilter] = useState('');
  const [rondaFilter, setRondaFilter] = useState('');
  const [asesorFilter, setAsesorFilter] = useState('');
  const { data: evalData } = useEvaluacionAdmin(asesoriaId);

  useEffect(() => {
    setLoading(true);
    dashboardService
      .getCambios(asesoriaId)
      .then((result) => setCambios(Array.isArray(result) ? result : []))
      .catch(() => setCambios([]))
      .finally(() => setLoading(false));
  }, [asesoriaId]);

  const safeCambios = useMemo(() => (Array.isArray(cambios) ? cambios : []), [cambios]);
  const uniqueCampos = useMemo(() => Array.from(new Set(safeCambios.map((c) => c.campo))).sort(), [safeCambios]);
  const uniqueRondas = useMemo(() => Array.from(new Set(safeCambios.map((c) => c.ronda ?? 1))).sort((a, b) => a - b), [safeCambios]);

  const formatCampo = (campo: string) => {
    if (campo === 'observaciones') return 'Observaciones';
    if (campo === 'planMejora') return 'Plan de Mejora';
    if (campo === 'fechaImplementacion') return 'Fecha Implementación';
    if (campo === 'evidenciaIds') return 'Evidencias';
    return campo;
  };

  const getNumeralNormInfo = (row: CambioFieldLog) => {
    if (!evalData || row.entidad !== 'REQUISITO') return '';
    const req = evalData.requisitos?.find((r: { requisitoId: string }) => r.requisitoId === row.entidadId);
    if (!req) return '';
    const ntc = evalData.ntcs?.find((n: { ntcId: number }) => n.ntcId === req.ntcId);
    return ntc?.nombre || `NTC ${req.ntcId}`;
  };

  const filtered = useMemo(() => {
    return safeCambios.filter((c) => {
      if (campoFilter && c.campo !== campoFilter) return false;
      if (rondaFilter && String(c.ronda ?? 1) !== rondaFilter) return false;
      if (asesorFilter && !(c.asesorNombre ?? '').toLowerCase().includes(asesorFilter.toLowerCase())) return false;
      return true;
    });
  }, [safeCambios, campoFilter, rondaFilter, asesorFilter]);

  const groupedByRonda = useMemo(() => {
    const map = new Map<number, CambioFieldLog[]>();
    for (const c of filtered) {
      const ronda = c.ronda ?? 1;
      if (!map.has(ronda)) map.set(ronda, []);
      map.get(ronda)!.push(c);
    }
    return Array.from(map.entries()).sort(([a], [b]) => b - a);
  }, [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner size="md" />
        <span className="ml-3 text-[13px] text-(--admin-text3)">Cargando trazabilidad...</span>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in-50 duration-200 space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={campoFilter}
          onChange={(e) => setCampoFilter(e.target.value)}
          className="admin-search-input text-[12px] w-auto min-w-40"
        >
          <option value="">Todos los campos</option>
          {uniqueCampos.map((c) => (
            <option key={c} value={c}>
              {formatCampo(c)}
            </option>
          ))}
        </select>
        <select
          value={rondaFilter}
          onChange={(e) => setRondaFilter(e.target.value)}
          className="admin-search-input text-[12px] w-auto min-w-32"
        >
          <option value="">Todas las rondas</option>
          {uniqueRondas.map((r) => (
            <option key={r} value={String(r)}>
              Ronda {r}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Filtrar por asesor..."
          value={asesorFilter}
          onChange={(e) => setAsesorFilter(e.target.value)}
          className="admin-search-input text-[12px] min-w-44"
        />
      </div>

      {groupedByRonda.length === 0 ? (
        <p className="text-[12px] text-gray-400 text-center py-8 italic">
          No hay cambios registrados. Los cambios se generan cuando el asesor envía a revisión.
        </p>
      ) : (
        <div className="space-y-4">
          {groupedByRonda.map(([ronda, rows]) => (
            <div key={ronda}>
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-2">
                <span className="bg-gray-100 px-2 py-0.5 rounded border border-gray-200">Ronda {ronda}</span>
                <span className="text-gray-400">{rows.length} cambios</span>
              </div>
              <div className="overflow-x-auto rounded-lg border border-(--admin-border)">
                <table className="w-full text-[12px]">
                  <thead className="bg-gray-50 border-b border-(--admin-border)">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Campo</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Antes</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Después</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Asesor</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Ronda</th>
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id} className="border-b border-(--admin-border) last:border-b-0 hover:bg-gray-50/50">
                        <td className="px-3 py-2 font-bold text-blue-700">
                          {formatCampo(row.campo)}
                          {(() => {
                            const info = getNumeralNormInfo(row);
                            if (!info) return null;
                            return <div className="text-[10px] text-gray-400 font-normal mt-0.5">{info}</div>;
                          })()}
                        </td>
                        <td className="px-3 py-2 text-gray-400 italic">
                          <span className="line-through max-w-[15ch] inline-block truncate align-bottom" title={row.valorAnterior ?? ''}>
                            {(row.valorAnterior ?? '—').slice(0, 60) || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-800">
                          <span className="max-w-[15ch] inline-block truncate align-bottom" title={row.valorNuevo ?? ''}>
                            {(row.valorNuevo ?? '—').slice(0, 60) || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500">{row.asesorNombre ?? '—'}</td>
                        <td className="px-3 py-2 text-gray-500">{row.ronda ?? 1}</td>
                        <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{formatDateTime((row as any).fecha ?? null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HistorialAsesoriasTab({ nit }: { nit: string }) {
  const [asesorias, setAsesorias] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchHistorial = async () => {
      setLoading(true);
      try {
        const res = await dashboardService.getEmpresa(nit);
        if (res.success && active) {
          setAsesorias(res.data.asesorias);
        }
      } catch (err) {
        console.error('Error fetching asesoria historial:', err);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchHistorial();
    return () => {
      active = false;
    };
  }, [nit]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner size="md" />
        <span className="ml-2 text-[12px] text-gray-500 font-medium">Cargando historial de asesorías...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-[11px] text-[#1e40af] flex items-center gap-2">
        <span>
          <strong>Tip de navegación:</strong> Puedes mantener presionada la tecla <strong>Shift</strong> al hacer clic en cualquier asesoría
          para abrirla en una pestaña nueva.
        </span>
      </div>
      <AsesoriaHistorialTable asesorias={asesorias} nitEmpresa={nit} getAsesoriaUrl={(nit, id) => `/empresas/${nit}/asesorias/${id}`} />
    </div>
  );
}

// ─── Main Detail Page ─────────────────────────────────

/** Ruta de solo lectura para el historial de UNA asesoría dentro de su empresa. */
export function buildHistorialAsesoriaPath(empresaNit: string, asesoriaId: number | string): string {
  return `/empresas/${empresaNit}/asesorias/${asesoriaId}`;
}

export function AdminRevisionDetailPage() {
  const { id, tab: tabParam } = useParams<{ id: string; tab: string }>();
  const navigate = useNavigate();
  const { pendientes, isLoading: loadingPendientes, error: errorPendientes, refetch } = usePendientesVerificacion();
  const { aprobar, isLoading: aprobando, error: errorAprobar } = useAprobarAsesoria();
  const { rechazar, isLoading: rechazando } = useRechazarAsesoria();
  const { marcar: marcarAutoAprobar, isLoading: marcandoAutoAprobar } = useMarcarAutoAprobar();
  const { desmarcar: desmarcarAutoAprobar, isLoading: desmarcandoAutoAprobar } = useDesmarcarAutoAprobar();
  const { simular: simularAprobar, isLoading: simulandoAprobar } = useSimularAprobarAsesoria();
  // Endpoint dedicado (simular-evaluar, no simular-aprobar) — a diferencia de "Aprobar Asesoría",
  // este SÍ tiene en cuenta que el flujo real de "Evaluar Asesoría" limpia los rechazos vigentes
  // (por reasignación) antes de aprobar, así que no los reporta como bloqueo si no corresponde.
  const { simular: simularEvaluar, isLoading: simulandoEvaluar } = useSimularEvaluarAsesoria();
  const showSecretPanel = useAdminSecretPanel();
  const [autoAprobarErrorModal, setAutoAprobarErrorModal] = useState<{ message: string; elementos: ElementoBloqueante[] } | null>(null);
  const [simulacionAprobar, setSimulacionAprobar] = useState<SimularAprobarResponse | null>(null);
  const [simulacionEvaluar, setSimulacionEvaluar] = useState<SimularAprobarResponse | null>(null);

  const validTabs: RevTab[] = ['evaluacion', 'evidencias', 'audios', 'trazabilidad', 'historial'];
  const activeTab: RevTab = validTabs.includes(tabParam as RevTab) ? (tabParam as RevTab) : 'evaluacion';
  const setActiveTab = (t: RevTab) => navigate(t === 'evaluacion' ? `/revision/${id}` : `/revision/${id}/${t}`, { replace: true });
  const [modalType, setModalType] = useState<'aprobar' | 'denegar' | null>(null);
  const [bloqueoAprobarModalOpen, setBloqueoAprobarModalOpen] = useState(false);
  // Datos de la empresa capturados al aprobar/denegar: tras la acción, la asesoría sale de la lista
  // de pendientes y `asesoria` pasa a undefined, así que el modal de redirect debe leer de aquí (no
  // de `asesoria`) y renderizarse ANTES del guard de "no encontrada".
  const [redirectInfo, setRedirectInfo] = useState<{ empresaNombre: string; nitId: string } | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasRejections, setHasRejections] = useState(false);
  const [showEvaluarWarning, setShowEvaluarWarning] = useState(false);
  const [evaluandoAsesoria, setEvaluandoAsesoria] = useState(false);
  const [evaluarError, setEvaluarError] = useState<string | null>(null);

  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);

  const asesoria = useMemo(() => pendientes.find((p) => p.asesoriaId === Number(id)), [pendientes, id]);

  // Si la asesoría no está en /pendientes, puede ser porque no existe O porque ya quedó resuelta
  // (preaprobada — autoAprobar — o aprobada/rechazada por otra vía). Antes ambos casos mostraban el
  // mismo "no se encontró la asesoría", un callejón sin salida para el segundo caso: el admin llegó
  // por una URL guardada a una asesoría que YA no necesita su revisión. Se consulta su ubicación
  // real y, si existe, se redirige al historial en vez de dejarlo en un error genérico.
  const [ubicacionAsesoria, setUbicacionAsesoria] = useState<'buscando' | 'no-encontrada' | 'redirigiendo' | null>(null);
  useEffect(() => {
    if (loadingPendientes || asesoria || !id) {
      setUbicacionAsesoria(null);
      return;
    }
    let cancelado = false;
    setUbicacionAsesoria('buscando');
    dashboardService
      .getUbicacionAsesoria(Number(id))
      .then((res) => {
        if (cancelado) return;
        setUbicacionAsesoria('redirigiendo');
        navigate(buildHistorialAsesoriaPath(res.empresaNit, res.asesoriaId), { replace: true });
      })
      .catch((err) => {
        if (cancelado) return;
        if (err instanceof ApiError && err.status === 404) {
          setUbicacionAsesoria('no-encontrada');
        } else {
          // Error de red/servidor: no se puede confirmar nada — se cae al mensaje genérico en
          // vez de dejar el spinner de "buscando" girando para siempre.
          setUbicacionAsesoria('no-encontrada');
        }
      });
    return () => {
      cancelado = true;
    };
  }, [loadingPendientes, asesoria, id, navigate]);
  const [historialRevision, setHistorialRevision] = useState<any[]>([]);
  const nit = asesoria?.empresa.nitId;
  useEffect(() => {
    if (nit && id) {
      dashboardService.getRevisionHistorial(nit, Number(id)).then((res) => {
        if (res.success) setHistorialRevision(res.historial);
      });
    }
  }, [nit, id]);
  const latestHistorialMap = useLatestHistorialMap(historialRevision);

  const tienePreviaPendienteEnLista = useMemo(() => {
    if (!asesoria) return false;
    if (asesoria.bloqueada !== undefined) return asesoria.bloqueada;
    return pendientes.some((p) => p.empresa.nitId === asesoria.empresa.nitId && p.asesoriaId < asesoria.asesoriaId);
  }, [pendientes, asesoria]);

  // Solo importa el estado de la HOJA de cada cadena de reemplazo (ver veredictoItemsVigentes):
  // contar filas crudas dejaba "Devolver" deshabilitado aunque todas las evidencias vigentes ya
  // estuvieran revisadas, porque las versiones intermedias quedan en PENDIENTE en la base.
  const veredictoItems = useMemo(() => veredictoItemsVigentes(asesoria ?? { evidenciasGenerales: [], audios: [] }), [asesoria]);
  const tieneEvidenciasOAudiosPendientes = veredictoItems.hayPendientes;
  const tieneEvidenciasOAudiosRechazados = veredictoItems.hayRechazados;

  const { data: evalDataDetail } = useEvaluacionAdmin(asesoria?.asesoriaId ? asesoria.asesoriaId : null);
  const detallesBloqueo = useMemo(
    () => getDetallesBloqueo(asesoria, evalDataDetail, latestHistorialMap),
    [asesoria, evalDataDetail, latestHistorialMap]
  );

  const { original: recomendacionesLimpias, rechazos } = useMemo(
    () => parseRecomendaciones(asesoria?.recomendaciones),
    [asesoria?.recomendaciones]
  );

  const tabs: { id: RevTab; label: string; count?: number }[] = useMemo(() => {
    if (!asesoria) return [];
    // El conteo debe contar CADENAS de reemplazo (una evidencia/audio con sus versiones = 1),
    // igual que lo que renderizan EvidenciasTab/AudiosTab. Contar filas crudas mostraba 9 cuando
    // en realidad hay 3 evidencias (las demás son reemplazos/borrados lógicos de la misma cadena).
    const nEvidencias = agruparCadenaArchivos(
      asesoria.evidenciasGenerales.filter((e) => e.estado !== 'ELIMINADO'),
      (e) => e.evidenciaId,
      (e) => e.reemplazaEvidenciaId ?? null
    ).length;
    const nAudios = agruparCadenaArchivos(
      asesoria.audios,
      (a) => a.audioId,
      (a) => a.reemplazaAudioId ?? null
    ).length;
    return [
      { id: 'evaluacion', label: 'Evaluación NTC' },
      { id: 'evidencias', label: 'Evidencias', count: nEvidencias },
      { id: 'audios', label: 'Audios', count: nAudios },
      { id: 'trazabilidad' as RevTab, label: 'Trazabilidad' },
      { id: 'historial', label: 'Historial asesorías' },
    ];
  }, [asesoria]);

  // Se renderiza ANTES de los guards de carga/no-encontrada: tras aprobar/denegar la asesoría deja
  // de estar en pendientes (asesoria => undefined), y sin este early-return el guard de abajo
  // mostraría "no se encontró la asesoría" en vez del modal de redirect.
  if (redirectInfo) {
    return (
      <Modal
        isOpen
        onClose={() => {
          setRedirectInfo(null);
          navigate('/revision');
        }}
        title="Acción Realizada"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            La revisión de la asesoría ha sido procesada correctamente. ¿Deseas ir al detalle de la empresa{' '}
            <strong>{redirectInfo.empresaNombre}</strong>?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              className="admin-btn border-gray-200 hover:bg-gray-50 text-gray-700 font-bold"
              onClick={() => {
                setRedirectInfo(null);
                navigate('/revision');
              }}
            >
              Volver a Revisiones
            </button>
            <button
              type="button"
              className="admin-btn primary bg-blue-50 text-blue-700 hover:bg-blue-100 font-bold border-transparent"
              onClick={() => {
                const nitId = redirectInfo.nitId;
                setRedirectInfo(null);
                navigate(`/empresas/${nitId}`);
              }}
            >
              Sí, ir a la empresa
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Mientras no haya asesoria y todavía no se confirmó que de verdad no existe (ubicacionAsesoria
  // aún no llegó a 'no-encontrada'), se sigue mostrando el spinner — evita el flash de "no se
  // encontró" mientras se consulta si en realidad está resuelta y hay que redirigir.
  if (loadingPendientes || (!asesoria && id && ubicacionAsesoria !== 'no-encontrada')) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner size="lg" />
        <span className="ml-3 text-[14px] text-(--admin-text3) font-medium">Cargando detalles de la asesoría...</span>
      </div>
    );
  }

  if (errorPendientes || !asesoria) {
    return (
      <div className="p-12 text-center">
        <XCircle className="w-12 h-12 text-(--admin-red) mx-auto mb-4 opacity-40 animate-bounce" />
        <h3 className="text-[16px] font-semibold text-(--admin-text) mb-1">No se encontró la asesoría</h3>
        <p className="text-[13px] text-(--admin-text3) mb-4">La asesoría solicitada no existe o ya no está pendiente de revisión.</p>
        <button type="button" className="admin-btn primary" onClick={() => navigate('/revision')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Volver a Revisor
        </button>
      </div>
    );
  }

  const cambio = (asesoria.resumenEvaluacion?.despues?.cumplimiento ?? 0) - (asesoria.resumenEvaluacion?.antes?.cumplimiento ?? 0);

  // El asesor que envió esta asesoría ya no es el titular actual de la empresa (reasignación) —
  // no hay a quién devolverle una corrección. El backend (revisarEvidencias/revisarAudios) ya
  // elimina en vez de rechazar en este caso, sin importar si el admin pasó por este botón — el
  // botón + warning son solo para que lo sepa ANTES de rechazar el primer ítem.
  const esAsesorDistinto =
    asesoria.estado === 'SIN_EVALUAR' || (asesoria.empresa.asesorId != null && asesoria.empresa.asesorId !== asesoria.asesor.asesorId);

  // Cualquier dato rechazado (campo NTC, evidencia o audio) impide aprobar la asesoría completa.
  // OJO: tienePreviaPendienteEnLista NO bloquea el botón — el backend ya maneja ese caso de forma
  // transparente (aprobarAsesoria cae a preaprobación automática cuando la anterior de la empresa
  // no está finalizada). El admin ve el mismo botón "Aprobar Asesoría" en todos los casos.
  const tieneRechazos = hasRejections || tieneEvidenciasOAudiosRechazados;
  const noSePuedeAprobar = tieneRechazos || tieneEvidenciasOAudiosPendientes;

  const handleAprobar = async () => {
    const res = await aprobar(asesoria.asesoriaId);
    if (res?.success) {
      setModalType(null);
      setRedirectInfo({ empresaNombre: asesoria.empresa.nombre, nitId: asesoria.empresa.nitId });
    }
  };

  const handleDenegar = async (motivo?: string, permiteSubirEvidenciasNuevas?: boolean) => {
    const ok = await rechazar(asesoria.asesoriaId, motivo, permiteSubirEvidenciasNuevas);
    if (ok) {
      setModalType(null);
      setRedirectInfo({ empresaNombre: asesoria.empresa.nombre, nitId: asesoria.empresa.nitId });
    }
  };

  // "Evaluar Asesoría" reemplaza a "Devolver"/"Aprobar" por completo cuando el asesor cambió —
  // ambos quedan ocultos (no tiene sentido devolverle una corrección a alguien que ya no es el
  // titular). Este único botón hace TODO el trabajo en un paso: 1) las evidencias/audios YA
  // rechazados (de antes de este cambio) se reenvían como RECHAZADO a los mismos endpoints de
  // siempre — el backend ya detecta la reasignación y los deja en ELIMINADO en vez de RECHAZADO
  // (no hay a quién devolverlos); 2) con los rechazos ya limpios, aprueba la asesoría directo. Si
  // todavía quedan ítems PENDIENTES sin revisar en las pestañas, la aprobación falla igual que
  // siempre — hay que resolverlos ahí primero (eso no cambia).
  const handleConfirmarEvaluarAsesoria = async () => {
    setEvaluandoAsesoria(true);
    setEvaluarError(null);
    try {
      const { evidenciasRechazadas, audiosRechazados } = veredictoItems;
      await Promise.all([
        evidenciasRechazadas.length > 0
          ? dashboardService.revisarEvidenciasAsesoria(
              asesoria.asesoriaId,
              evidenciasRechazadas.map((e) => ({ evidenciaId: e.evidenciaId, estado: 'RECHAZADO' as const }))
            )
          : Promise.resolve(),
        audiosRechazados.length > 0
          ? dashboardService.revisarAudiosAsesoria(
              asesoria.asesoriaId,
              audiosRechazados.map((a) => ({ audioId: a.audioId, estado: 'RECHAZADO' as const }))
            )
          : Promise.resolve(),
        // Rechazos de MATRIZ (requisitos/campos de terna): a diferencia de evidencias/audios, aquí
        // no hay archivo que borrar — se revierte el valor al de la asesoría anterior y se marca
        // Aprobado. No-op (resueltos: 0) si el asesor no cambió; el backend valida igual.
        dashboardService.resolverRechazosMatrizPorReasignacion(asesoria.asesoriaId),
      ]);

      const res = await aprobar(asesoria.asesoriaId);
      if (res?.success) {
        setShowEvaluarWarning(false);
        setRedirectInfo({ empresaNombre: asesoria.empresa.nombre, nitId: asesoria.empresa.nitId });
      } else {
        refetch();
        setEvaluarError(
          'Se eliminaron los rechazos previos, pero todavía quedan ítems pendientes de revisar (evidencias/audios/requisitos) — resuélvelos en las pestañas de abajo y vuelve a intentar "Evaluar Asesoría".'
        );
      }
    } finally {
      setEvaluandoAsesoria(false);
    }
  };

  // El boton se muestra siempre que el sysadmin suplantando al admin lo revele con Ctrl+. — si la
  // asesoria no cumple los requisitos (rechazos, pendientes sin calificar, o no hay cadena que
  // bloquee), simplemente queda deshabilitado en vez de ocultarse (el backend igual lo rechazaria).
  const mostrarBotonAutoAprobar = showSecretPanel;
  const elegibleParaAutoAprobar = tienePreviaPendienteEnLista && !tieneRechazos && !tieneEvidenciasOAudiosPendientes;

  const handleToggleAutoAprobar = async () => {
    if (asesoria.autoAprobar) {
      const res = await desmarcarAutoAprobar(asesoria.asesoriaId);
      if (res?.success) refetch();
    } else {
      const res = await marcarAutoAprobar(asesoria.asesoriaId);
      if (res?.success) {
        refetch();
      } else if (res) {
        setAutoAprobarErrorModal({
          message: res.message ?? 'No se puede marcar para auto-aprobacion.',
          elementos: res.elementos ?? [],
        });
      }
    }
  };

  // Lleva al admin exactamente al ítem que impide aprobar (evidencia/audio pendiente o rechazado, o campo NTC rechazado).
  const irABloqueo = () => {
    const veredicto = veredictoItemsVigentes(asesoria);
    if (veredicto.evidenciasPendientes.length > 0 || veredicto.evidenciasRechazadas.length > 0) {
      return setActiveTab('evidencias');
    }
    if (veredicto.audiosPendientes.length > 0 || veredicto.audiosRechazados.length > 0) {
      return setActiveTab('audios');
    }
    return setActiveTab('evaluacion');
  };

  const content = (
    <div className="space-y-6 animate-in fade-in-50 duration-300 font-['DM_Sans']">
      {/* Top Glassmorphic Navigation Bar */}
      <div
        className="flex items-center justify-between gap-4 bg-white/70 backdrop-blur-md p-4 rounded-xl border border-white/40 shadow-xs sticky z-40"
        style={{ top: isFullscreen ? '0px' : 'calc(var(--admin-offset, 0px) + var(--admin-top-bar-height, 53px))' }}
      >
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="p-2 rounded-lg hover:bg-gray-100 border border-gray-200 transition-colors"
            onClick={() => navigate('/revision')}
          >
            <ArrowLeft className="w-4 h-4 text-gray-700" />
          </button>
          <div>
            <h1 className="text-[18px] font-bold text-gray-900 tracking-tight">{asesoria.empresa.nombre}</h1>
            <p className="text-[12px] text-gray-500 font-medium mt-0.5">
              NIT: {asesoria.empresa.nitId} · Asesor: {asesoria.asesor.nombre}
            </p>
            {showSecretPanel && (
              <span className="inline-block mt-1 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                Modo debug · Asesoría ID: {asesoria.asesoriaId}
              </span>
            )}
          </div>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="admin-btn text-[12px] font-bold bg-[#eaf4fe] hover:bg-[#d5e6fd] text-[#2b6cb0] border-transparent"
            onClick={() => navigate(`/empresas/${asesoria.empresa.nitId}`)}
          >
            Ver Empresa
          </button>
          {!esAsesorDistinto && (
            <div className="relative group/deny-btn">
              <button
                type="button"
                disabled={tieneEvidenciasOAudiosPendientes}
                className={`admin-btn text-[12px] font-bold bg-[#fdf0ee] text-[#c0392b] border-transparent transition-opacity ${
                  tieneEvidenciasOAudiosPendientes ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#fae3df]'
                }`}
                onClick={() => !tieneEvidenciasOAudiosPendientes && setModalType('denegar')}
              >
                Devolver Asesoría
              </button>
              {tieneEvidenciasOAudiosPendientes && (
                <div className="absolute top-full right-0 mt-2 w-80 pointer-events-none opacity-0 group-hover/deny-btn:opacity-100 transition-opacity duration-150 z-50">
                  <div className="flex justify-end pr-4">
                    <div className="w-2.5 h-2.5 bg-gray-900 rotate-45 -mb-1.5 relative z-10 shadow-sm" />
                  </div>
                  <div className="bg-gray-900 text-white text-[11px] leading-relaxed px-3.5 py-2.5 rounded-lg shadow-xl space-y-1">
                    <p className="font-bold text-amber-300 mb-0.5">Elementos sin revisar:</p>
                    <ul className="list-disc pl-3.5 space-y-0.5 text-gray-200">
                      {detallesBloqueo.audiosPendientesCount > 0 && <li>Audios: {detallesBloqueo.audiosPendientesCount} audio(s)</li>}
                      {detallesBloqueo.evidenciasPendientes.length > 0 && (
                        <li>Evidencias: {detallesBloqueo.evidenciasPendientes.join(', ')}</li>
                      )}
                    </ul>
                    <span className="mt-1 block text-[10px] font-semibold text-gray-300 border-t border-gray-800 pt-1">
                      Revisa todos los ítems antes de devolver la asesoría.
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          {esAsesorDistinto && (
            <button
              type="button"
              className="admin-btn text-[12px] font-bold bg-amber-50 text-amber-700 border-transparent hover:bg-amber-100"
              onClick={() => {
                setSimulacionEvaluar(null);
                setShowEvaluarWarning(true);
              }}
              title="El asesor titular actual de la empresa es distinto al que envió esta asesoría"
            >
              Evaluar Asesoría
            </button>
          )}
          {mostrarBotonAutoAprobar && (
            <button
              type="button"
              disabled={marcandoAutoAprobar || desmarcandoAutoAprobar || (!asesoria.autoAprobar && !elegibleParaAutoAprobar)}
              className={`admin-btn text-[12px] font-bold border-transparent transition-opacity disabled:opacity-40 ${
                asesoria.autoAprobar
                  ? 'bg-(--admin-blue-bg) text-(--admin-blue) hover:bg-[#d5e6fd]'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              onClick={handleToggleAutoAprobar}
              title={
                asesoria.autoAprobar
                  ? 'Se aprobara sola en cuanto la asesoria anterior de la empresa se resuelva'
                  : elegibleParaAutoAprobar
                    ? 'Se aprobara sola en cuanto la asesoria anterior de la empresa se resuelva'
                    : 'No elegible: hay rechazos o elementos sin revisar, o no hay una asesoria anterior bloqueando'
              }
            >
              {marcandoAutoAprobar || desmarcandoAutoAprobar
                ? 'Guardando...'
                : asesoria.autoAprobar
                  ? 'Quitar marca de auto-aprobar'
                  : 'Marcar para auto-aprobar'}
            </button>
          )}
          {!esAsesorDistinto && (
            <div className="relative group/approve-btn">
              <button
                type="button"
                disabled={noSePuedeAprobar && !showSecretPanel}
                className={`admin-btn primary text-[12px] font-bold bg-[#eaf5ee] text-[#2d7a4f] border-transparent transition-opacity ${
                  noSePuedeAprobar && !showSecretPanel ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#d5edd9] cursor-pointer'
                }`}
                onClick={() => {
                  if (noSePuedeAprobar && !showSecretPanel) irABloqueo();
                  else setModalType('aprobar');
                }}
              >
                Aprobar Asesoría
              </button>
              {noSePuedeAprobar && (
                <div className="absolute top-full right-0 mt-2 w-80 pointer-events-none opacity-0 group-hover/approve-btn:opacity-100 transition-opacity duration-150 z-50">
                  <div className="flex justify-end pr-4">
                    <div className="w-2.5 h-2.5 bg-gray-900 rotate-45 -mb-1.5 relative z-10 shadow-sm" />
                  </div>
                  <div className="bg-gray-900 text-white text-[11px] leading-relaxed px-3.5 py-2.5 rounded-lg shadow-xl">
                    <div className="space-y-1.5">
                      {detallesBloqueo.totalPendientesCount > 0 && (
                        <div>
                          <p className="font-bold text-amber-300 mb-0.5">Elementos sin revisar ({detallesBloqueo.totalPendientesCount}):</p>
                          <ul className="list-disc pl-3.5 space-y-0.5 text-gray-200">
                            {detallesBloqueo.audiosPendientesCount > 0 && <li>Audios: {detallesBloqueo.audiosPendientesCount} audio(s)</li>}
                            {detallesBloqueo.evidenciasPendientes.length > 0 && (
                              <li>Evidencias: {detallesBloqueo.evidenciasPendientes.join(', ')}</li>
                            )}
                            {detallesBloqueo.requisitosPendientesNumerals.length > 0 && (
                              <li>Requisitos: {detallesBloqueo.requisitosPendientesNumerals.join(', ')}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {detallesBloqueo.totalRechazadosCount > 0 && (
                        <div className={detallesBloqueo.totalPendientesCount > 0 ? 'mt-1.5 pt-1.5 border-t border-gray-800' : ''}>
                          <p className="font-bold text-red-300 mb-0.5">Elementos rechazados ({detallesBloqueo.totalRechazadosCount}):</p>
                          <ul className="list-disc pl-3.5 space-y-0.5 text-gray-200">
                            {detallesBloqueo.audiosRechazadosCount > 0 && (
                              <li>Audios: {detallesBloqueo.audiosRechazadosCount} audio(s) rechazado(s)</li>
                            )}
                            {detallesBloqueo.evidenciasRechazadas.length > 0 && (
                              <li>Evidencias: {detallesBloqueo.evidenciasRechazadas.join(', ')}</li>
                            )}
                            {detallesBloqueo.requisitosRechazadosNumerals.length > 0 && (
                              <li>Requisitos: {detallesBloqueo.requisitosRechazadosNumerals.join(', ')}</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {detallesBloqueo.totalPendientesCount === 0 && detallesBloqueo.totalRechazadosCount === 0 && (
                        <div>
                          <p className="font-bold text-red-300 mb-0.5">Elementos rechazados o pendientes:</p>
                          <p className="text-gray-200 text-[11px]">Hay elementos en la evaluación con observaciones o rechazo activo.</p>
                        </div>
                      )}
                      <span className="mt-1 block text-[10px] font-semibold text-gray-300 border-t border-gray-800 pt-1">
                        Haz clic para ir al ítem →
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            className="admin-btn text-[12px] p-2"
            onClick={() => setIsFullscreen((prev) => !prev)}
            title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Statistics widgets */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Compliance widget */}
        <div className="bg-white p-4 rounded-xl border border-(--admin-border) shadow-xs flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5 text-(--admin-green)" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Cumplimiento de Normas</div>
            <div className="flex items-baseline gap-2 mt-1">
              <OficialPrefix
                oficial={asesoria.resumenEvaluacion?.antes?.cumplimientoOficial}
                proyectado={asesoria.resumenEvaluacion?.antes?.cumplimiento ?? 0}
                format={(n) => `${n.toFixed(1)}%`}
              />
              <Tooltip label="Cumplimiento antes de esta asesoría (proyectado)" align="left">
                <span className="text-[16px] font-bold text-gray-800">{asesoria.resumenEvaluacion?.antes?.cumplimiento?.toFixed(1)}%</span>
              </Tooltip>
              <span className="text-[12px] text-gray-400">→</span>
              <Tooltip label="Cumplimiento después de esta asesoría (proyectado)" align="center">
                <span className="text-[18px] font-black text-(--admin-green)">
                  {asesoria.resumenEvaluacion?.despues?.cumplimiento?.toFixed(1)}%
                </span>
              </Tooltip>
              {cambio !== 0 && (
                <Tooltip label="Cambio de cumplimiento que propone esta asesoría" align="right">
                  <span className={`text-[11px] font-bold ml-1 ${cambio > 0 ? 'text-(--admin-green)' : 'text-(--admin-red)'}`}>
                    ({cambio > 0 ? `+${cambio.toFixed(1)}%` : `${cambio.toFixed(1)}%`})
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {/* Hours widget */}
        <div className="bg-white p-4 rounded-xl border border-(--admin-border) shadow-xs flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
            <Clock className="w-5 h-5 text-(--admin-blue)" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Horas Asignadas</div>
            <div className="flex items-baseline gap-2 mt-1">
              {asesoria.horasAntes != null && asesoria.horasDespues != null ? (
                <>
                  <OficialPrefix oficial={asesoria.horasOficial} proyectado={asesoria.horasAntes} format={formatHours} />
                  <Tooltip label="Horas acumuladas antes de esta asesoría (proyectado)" align="left">
                    <span className="text-[14px] text-gray-500">
                      {formatHours(asesoria.horasAntes)} ({asesoria.porcentajeHorasAntes?.toFixed(1)}%)
                    </span>
                  </Tooltip>
                  <span className="text-[12px] text-gray-400">→</span>
                  <Tooltip label="Horas acumuladas después de esta asesoría (proyectado)" align="right">
                    <span className="text-[16px] font-bold text-(--admin-green)">
                      {formatHours(asesoria.horasDespues)} ({asesoria.porcentajeHorasDespues?.toFixed(1)}%)
                    </span>
                  </Tooltip>
                </>
              ) : (
                <span className="text-[16px] font-bold text-gray-800">
                  {formatHours(asesoria.horas)} (+{((asesoria.horas / 320) * 100).toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Date widget */}
        <div className="bg-white p-4 rounded-xl border border-(--admin-border) shadow-xs flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
            <Calendar className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Fechas de Gestión</div>
            <div className="text-[13px] font-semibold text-gray-700 mt-1 space-y-0.5">
              <div>
                <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wide mr-1">Inicio</span>
                <Tooltip label="Fecha en la que el asesor inició esta asesoría" align="right">
                  <span>{formatDate(asesoria.fechaInicio)}</span>
                </Tooltip>
              </div>
              <div>
                <span className="text-[10px] font-normal text-gray-400 uppercase tracking-wide mr-1">Subida</span>
                <Tooltip label="Fecha en la que el asesor envió esta asesoría a revisión" align="right">
                  <span>
                    {asesoria.fechaFin ? formatDate(asesoria.fechaFin) : <span className="text-gray-400 font-normal">En curso</span>}
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compromisos / Conclusiones */}
      {(asesoria.compromisos || asesoria.conclusiones) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {asesoria.compromisos && (
            <div className="bg-white rounded-xl border border-(--admin-border) shadow-xs p-4">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Compromisos</div>
              <p className="text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">{asesoria.compromisos}</p>
            </div>
          )}
          {asesoria.conclusiones && (
            <div className="bg-white rounded-xl border border-(--admin-border) shadow-xs p-4">
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">Conclusiones</div>
              <p className="text-[13px] text-gray-700 whitespace-pre-wrap leading-relaxed">{asesoria.conclusiones}</p>
            </div>
          )}
        </div>
      )}

      {/* Historial de Rechazos */}
      {rechazos.length > 0 && (
        <div className="mt-4 p-4 rounded-xl bg-red-50 border border-red-200">
          <div className="text-[10px] uppercase tracking-wider mb-3 flex items-center gap-2 font-bold text-red-600">
            <AlertCircle className="w-3.5 h-3.5" />
            Historial de Rechazos ({rechazos.length})
          </div>
          <div className="space-y-3">
            {rechazos.map((rechazo, idx) => (
              <div key={idx} className="bg-white/60 p-3 rounded-lg border border-red-100">
                <div className="text-[10px] text-red-600 font-mono mb-1">{rechazo.fecha}</div>
                <div className="text-[12px] text-red-900 font-medium whitespace-pre-wrap leading-relaxed">
                  {rechazo.motivo || <span className="italic opacity-60">Sin motivo especificado</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs Container */}
      <div className="border border-(--admin-border) rounded-xl overflow-hidden bg-white shadow-xs">
        <div className="flex bg-(--admin-surface2) border-b border-(--admin-border) px-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 text-[13px] font-bold border-b-2 transition-all flex items-center gap-2 ${
                activeTab === tab.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full text-[10px] font-semibold">{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Active Tab Workspace */}
        <div className="p-4">
          {activeTab === 'evaluacion' && (
            <EvalTab
              asesoriaId={asesoria.asesoriaId}
              nit={asesoria.empresa.nitId}
              refetch={refetch}
              isFullscreen={isFullscreen}
              onHasRejections={setHasRejections}
            />
          )}
          {activeTab === 'evidencias' && <EvidenciasTab asesoria={asesoria} refetch={refetch} />}
          {activeTab === 'audios' && <AudiosTab asesoria={asesoria} refetch={refetch} />}
          {activeTab === 'trazabilidad' && <TrazabilidadTab asesoriaId={asesoria.asesoriaId} />}
          {activeTab === 'historial' && <HistorialAsesoriasTab nit={asesoria.empresa.nitId} />}
        </div>
      </div>

      {/* Action Modals */}
      {modalType === 'aprobar' && (
        <AprobarModal
          asesoria={asesoria}
          onClose={() => {
            setModalType(null);
            setSimulacionAprobar(null);
          }}
          onConfirm={handleAprobar}
          isLoading={aprobando}
          error={errorAprobar}
          showSecretPanel={showSecretPanel}
          onSimular={async () => setSimulacionAprobar(await simularAprobar(asesoria.asesoriaId))}
          simulando={simulandoAprobar}
          simulacionResultado={simulacionAprobar}
        />
      )}
      {modalType === 'denegar' && (
        <DenegarModal asesoria={asesoria} onClose={() => setModalType(null)} onConfirm={handleDenegar} isLoading={rechazando} />
      )}

      <Modal isOpen={autoAprobarErrorModal !== null} onClose={() => setAutoAprobarErrorModal(null)} title="No se puede marcar">
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{autoAprobarErrorModal?.message}</p>
          {autoAprobarErrorModal && autoAprobarErrorModal.elementos.length > 0 && (
            <ul className="space-y-1.5 max-h-64 overflow-y-auto">
              {autoAprobarErrorModal.elementos.map((el, i) => (
                <li key={`${el.tipo}-${el.id}-${i}`} className="text-sm bg-gray-50 border border-gray-100 rounded px-3 py-2">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mr-1.5">{el.tipo}</span>
                  <span className="text-gray-800">{el.nombre}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              className="admin-btn border-gray-200 hover:bg-gray-50 text-gray-700 font-bold"
              onClick={() => setAutoAprobarErrorModal(null)}
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showEvaluarWarning} onClose={() => setShowEvaluarWarning(false)} title="Evaluar Asesoría" size="md">
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-[13px] text-amber-900 leading-relaxed space-y-2">
              <p>
                El asesor titular actual de la empresa ya no es <strong>{asesoria.asesor.nombre}</strong>, quien envió esta asesoría — no
                hay a quién devolverle una corrección.
              </p>
              <p>
                Al continuar: cualquier evidencia o audio <strong>ya rechazado se elimina</strong> (no queda para corregir ni volver a
                subir); cualquier requisito o campo de la matriz (Plan de Mejora, Observaciones, Fecha de implementación) ya rechazado{' '}
                <strong>vuelve al valor de la asesoría anterior y se marca aprobado</strong>; y la asesoría se{' '}
                <strong>aprueba directo</strong>. Los botones "Devolver" y "Aprobar" quedan ocultos porque ninguno de los dos tiene sentido
                cuando el asesor ya fue reasignado.
              </p>
            </div>
          </div>

          {(veredictoItems.evidenciasRechazadas.length > 0 || veredictoItems.audiosRechazados.length > 0) && (
            <div className="border border-gray-200 rounded-lg p-3">
              <p className="text-[12px] font-bold text-gray-700 mb-2">
                Se van a eliminar {veredictoItems.evidenciasRechazadas.length + veredictoItems.audiosRechazados.length} ítem(s)
                rechazado(s):
              </p>
              <ul className="text-[12px] text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                {veredictoItems.evidenciasRechazadas.map((e) => (
                  <li key={`ev-${e.evidenciaId}`} className="flex items-center gap-1.5">
                    <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">Evidencia</span> {e.nombre}
                  </li>
                ))}
                {veredictoItems.audiosRechazados.map((a) => (
                  <li key={`au-${a.audioId}`} className="flex items-center gap-1.5">
                    <span className="text-[9px] font-bold uppercase tracking-wide text-gray-400">Audio</span> {formatDate(a.fechaGrabacion)}{' '}
                    · {formatDuracionCorta(a.duracionSeg)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {evaluarError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-800">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{evaluarError}</span>
            </div>
          )}

          {showSecretPanel && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700">Modo debug</span>
                <button
                  type="button"
                  onClick={async () => setSimulacionEvaluar(await simularEvaluar(asesoria.asesoriaId))}
                  disabled={simulandoEvaluar}
                  className="admin-btn text-[11px] py-1 px-2.5 border-amber-300 bg-white text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                >
                  {simulandoEvaluar ? 'Simulando...' : 'Simular evaluar asesoría'}
                </button>
              </div>
              <p className="text-[10px] text-amber-700/80">
                Valida el flujo completo de "Evaluar Asesoría" sin persistir ningún cambio — a diferencia del simulador de "Aprobar
                Asesoría", este SÍ tiene en cuenta que los rechazos vigentes de evidencias/audios/matriz se limpian solos antes de aprobar,
                así que no los reporta como bloqueo si no corresponde.
              </p>
              {simulacionEvaluar && (
                <div
                  className={`mt-2 p-2.5 rounded-md text-[11px] ${
                    simulacionEvaluar.ok ? 'bg-(--admin-green-bg) text-(--admin-green)' : 'bg-(--admin-red-bg) text-(--admin-red)'
                  }`}
                >
                  <span className="font-semibold">
                    {simulacionEvaluar.ok
                      ? 'Aprobaría correctamente.'
                      : (simulacionEvaluar.mensaje ?? simulacionEvaluar.message ?? 'No aprobaría.')}
                  </span>
                  {!simulacionEvaluar.ok && simulacionEvaluar.elementos && simulacionEvaluar.elementos.length > 0 && (
                    <ul className="mt-1.5 space-y-1 max-h-40 overflow-y-auto">
                      {simulacionEvaluar.elementos.map((el, i) => (
                        <li key={`${el.tipo}-${el.id}-${i}`} className="bg-white/60 rounded px-2 py-1">
                          <span className="font-bold uppercase tracking-wide text-[9px] mr-1.5 whitespace-pre-line">{el.tipo}</span>
                          {el.nombre}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={evaluandoAsesoria}
              className="admin-btn border-gray-200 hover:bg-gray-50 text-gray-700 font-bold disabled:opacity-40"
              onClick={() => setShowEvaluarWarning(false)}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={evaluandoAsesoria}
              className="admin-btn primary bg-amber-600 hover:bg-amber-700 text-white font-bold border-transparent disabled:opacity-40"
              onClick={handleConfirmarEvaluarAsesoria}
            >
              {evaluandoAsesoria ? 'Procesando...' : 'Entendido, evaluar y aprobar'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );

  if (isFullscreen) {
    return <div className="fixed inset-0 z-100 overflow-y-auto bg-white p-4">{content}</div>;
  }

  return content;
}
